import { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase.js';
import {
  getTodayDebate, getArchive, getUpcomingDebate, getUserVote, getVoteCounts, castVote, changeVote,
  getCommentsWithStatus, postComment, updateComment, deleteComment, upvoteComment, removeUpvote, getUserUpvotes,
  getUserProfile, isAdmin, seedAIComment, getAIPersonas,
  subscribeToComments, subscribeToUpvotes,
  executeSideSwitch, hasUserSwitched, logPersuasionSignal,
  getAllDebatesAdmin, createDebate, updateDebate, deleteDebate, getRecentCommentsAdmin,
  getMindsChangedCount, getCommentsByDisplayName,
  getVoterEmails, sendEmailToAddresses, getVoterHistory,
  getDebateVoterNames, getAppSetting, setAppSetting,
} from './lib/api.js';
import { moderateComment, generateAIComment } from './lib/moderation.js';
import { generateShareText, getXShareUrl } from './lib/share.js';

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** Deterministic integer hash of a string (djb2 variant) */
function hashStr(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return Math.abs(h);
}

/** Strip HTML tags and decode basic entities to prevent XSS / injection in comments */
function stripHtml(str) {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#x27;/g, "'")
    .replace(/javascript:/gi, '').replace(/on\w+=/gi, '');
}

// ─── Sponsors ─────────────────────────────────────────────────────────────────
const SPONSORS = [
  { name: 'Liquid Death',    tagline: 'Murder Your Thirst',         letter: 'L', color: '#1B3B5A', url: 'https://liquiddeath.com' },
  { name: 'Death Wish Coffee', tagline: "World's Strongest Coffee", letter: 'D', color: '#2a0808', url: 'https://deathwishcoffee.com' },
  { name: 'Brunt Workwear',  tagline: 'Work boots built for workers',letter: 'B', color: '#7a3a10', url: 'https://bruntworkwear.com' },
  { name: 'Cometeer',        tagline: 'The future of coffee',        letter: 'C', color: '#003a5a', url: 'https://cometeer.com' },
  { name: 'MSCHF',           tagline: 'Make something cool happen',  letter: 'M', color: '#aa0000', url: 'https://mschf.com' },
  { name: 'Tabs Chocolate',  tagline: 'The 2-person experience',     letter: 'T', color: '#3d1a00', url: 'https://tabschocolate.com' },
  { name: 'Lume Deodorant',  tagline: 'Whole body odor control',     letter: 'L', color: '#1a3a1a', url: 'https://lumedeodorant.com' },
  { name: 'Cuts Clothing',   tagline: 'Premium performance clothing',letter: 'C', color: '#111122', url: 'https://cuts.co' },
  { name: 'Topicals',        tagline: 'Skin that speaks for itself', letter: 'T', color: '#3a0a3a', url: 'https://thetopicals.com' },
  { name: 'Jolie',           tagline: 'Filtered shower heads',       letter: 'J', color: '#0a2a3a', url: 'https://jolieskinco.com' },
];

// ─── Sample questions ─────────────────────────────────────────────────────────
const SAMPLE_QUESTIONS = [
  'Coffee ☕ or Tea 🍵?',
  'Morning person 🌅 or Night owl 🦉?',
  'Work from home or the office?',
  'Cats 🐱 or Dogs 🐶?',
  'Instagram or TikTok?',
  'Hot weather ☀️ or Cold weather ❄️?',
  'Text or call?',
  'Netflix 🎬 or Reading 📚?',
  'City life 🌆 or Countryside 🌿?',
  'Logic 🧠 or Intuition 💡?',
  'Save money 💰 or Live for now?',
  'Pineapple on pizza 🍕 yes or no?',
  'Introvert 🪴 or Extrovert 🎊?',
  'Quality or Quantity?',
  'Early bird or sleep in?',
  'Rules or Freedom?',
  'Head ❄️ or Heart 🔥?',
  'Planned or Spontaneous?',
  'Android or iPhone?',
  'Cook at home or eat out?',
  'Sneakers or Boots?',
  'Gym or Outdoors?',
];

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,700;0,9..40,900;1,9..40,900&display=swap');

  * { box-sizing: border-box; }
  body { margin: 0; background: #0a0a1a; }

  /* ── Layout: single column ── */
  .vs-layout {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: #070715;
    background-image:
      radial-gradient(ellipse 80% 50% at 50% -10%, rgba(79,196,184,0.09) 0%, transparent 65%),
      radial-gradient(ellipse 60% 40% at 100% 60%, rgba(232,99,90,0.06) 0%, transparent 60%),
      radial-gradient(ellipse 50% 35% at 0% 80%, rgba(100,80,200,0.05) 0%, transparent 60%),
      linear-gradient(160deg, #07071a 0%, #080818 50%, #070712 100%);
  }
  .vs-center { width: 100%; display: flex; flex-direction: column; }
  /* Explainer panel below center */
  .vs-explainer {
    width: 100%;
    max-width: 680px;
    margin: 0 auto;
    padding: 16px 16px 96px;
    border-top: 1px solid #12122a;
  }

  /* ── Logo animations ── */
  .pas-logo { display: inline-flex; align-items: center; line-height: 1; white-space: nowrap; overflow: visible; padding: 4px 0; }
  .pas-logo-wrap { overflow: visible !important; display: inline-block; padding: 2px 4px; }
  @keyframes aPivot {
    0%   { transform: rotate(-4deg); }
    50%  { transform: rotate(4deg);  }
    100% { transform: rotate(-4deg); }
  }
  .pas-a { animation: aPivot 2.4s ease-in-out infinite; transform-origin: bottom center; display: inline-block; }
  @keyframes logoBreath {
    0%, 100% { opacity: 0.92; }
    50%       { opacity: 1; }
  }
  .pas-logo-wrap { animation: logoBreath 4s ease-in-out infinite; display: inline-block; overflow: visible; }
  @keyframes diamondShine {
    0%   { background-position: 200% center; }
    100% { background-position: -200% center; }
  }
  .pas-a-diamond {
    background: linear-gradient(105deg, #ffffff 0%, #c8f0ff 15%, #ffe8f8 28%, #ffffff 38%, #fff9c4 50%, #c8f0ff 62%, #f8c8ff 75%, #ffffff 85%, #c8f0ff 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: aPivot 2.4s ease-in-out infinite, diamondShine 3s linear infinite;
    transform-origin: bottom center;
    display: inline-block;
    padding: 0 5px;
    overflow: visible;
    /* No filter here — it conflicts with background-clip:text in some browsers */
  }

  /* ── Vote flash ── */
  .vf-overlay { animation: vfFade 2.6s ease-out forwards; }
  @keyframes vfFade {
    0%   { opacity:0; } 5% { opacity:1; } 70% { opacity:0.9; } 100% { opacity:0; }
  }
  .vf-bg { animation: vfFade 2.6s ease-out forwards; }
  .vf-label { animation: vfLabel 2.6s cubic-bezier(.23,1,.32,1) forwards; }
  @keyframes vfLabel {
    0%   { transform: scale(5) skewX(-10deg); opacity:0; }
    10%  { transform: scale(0.92) skewX(-5deg); opacity:1; }
    25%  { transform: scale(1.04) skewX(-3deg); }
    35%  { transform: scale(1) skewX(-4deg); }
    75%  { transform: scale(1) skewX(-4deg); opacity:1; }
    100% { transform: scale(1.08) skewX(-4deg); opacity:0; }
  }
  .vf-chosen { animation: vfChosen 2.6s cubic-bezier(.23,1,.32,1) forwards; }
  @keyframes vfChosen {
    0%   { transform: translateY(50px) scale(0.5); opacity:0; }
    15%  { transform: translateY(-6px) scale(1.06); opacity:1; }
    28%  { transform: translateY(3px) scale(0.98); }
    38%  { transform: translateY(0) scale(1); }
    75%  { transform: translateY(0) scale(1); opacity:1; }
    100% { transform: translateY(-18px) scale(1); opacity:0; }
  }
  .vf-bolt { animation: vfBolt 1.9s ease-out forwards; }
  @keyframes vfBolt {
    0%   { transform: translate(-50%,-50%) scale(0) rotate(var(--r)); opacity:1; }
    20%  { opacity:1; }
    100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(1.4) rotate(var(--r)); opacity:0; }
  }
  .vf-particle { animation: vfParticle 1.7s ease-out forwards; }
  @keyframes vfParticle {
    0%   { transform: translate(-50%,-50%); opacity:1; }
    100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))); opacity:0; }
  }
  .vf-ring { animation: vfRing 1.5s ease-out forwards; }
  @keyframes vfRing {
    0%   { transform: translate(-50%,-50%) scale(0); opacity:0.9; }
    100% { transform: translate(-50%,-50%) scale(7); opacity:0; }
  }
  .vf-ring2 { animation: vfRing 2s 0.25s ease-out forwards; opacity:0; }
  .vf-shake { animation: vfShake 0.4s ease-out; }
  @keyframes vfShake {
    0%,100% { transform:translate(0,0); }
    10% { transform:translate(-5px,3px); } 20% { transform:translate(5px,-3px); }
    30% { transform:translate(-4px,4px); } 40% { transform:translate(4px,-4px); }
    50% { transform:translate(-2px,2px); } 60% { transform:translate(2px,-2px); }
  }

  /* ── Questions panel ── */
  @keyframes aqFade { 0% { opacity:0; transform:translateY(12px); } 30% { opacity:1; transform:translateY(0); } }

  /* ── Sponsor ── */
  @keyframes sponsorPulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(247,201,72,0); }
    50%      { box-shadow: 0 0 18px 2px rgba(247,201,72,0.10); }
  }
  .sponsor-badge { animation: sponsorPulse 3.2s ease-in-out infinite; }

  /* ── Auth side panels: hide on narrow screens ── */
  @media (max-width: 900px) {
    .auth-side-panel { display: none !important; }
  }

  /* ── Vote buttons ── */
  .vote-btn { transition: transform 0.12s, filter 0.12s, box-shadow 0.12s; }
  .vote-btn:hover  { transform: scale(1.03) skewX(-1deg); filter: brightness(1.12); }
  .vote-btn:active { transform: scale(0.97); }

  /* ── Debate card gradient border ── */
  .debate-card-wrap {
    background: linear-gradient(135deg, #4fc4b830, #f7c94820, #e8635a30);
    padding: 1.5px;
    border-radius: 20px;
    margin-bottom: 14px;
  }
  .debate-card-inner {
    background: linear-gradient(150deg, #101028 0%, #0d0d22 100%);
    border-radius: 19px;
    padding: 24px 18px;
    position: relative;
    overflow: hidden;
  }
  .debate-card-inner::before {
    content: '';
    position: absolute; top:0; left:0; right:0; height:2px;
    background: linear-gradient(90deg, #4fc4b8, #f7c948, #e8635a);
    border-radius: 19px 19px 0 0;
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PAL = ['#e05252','#e07c52','#c8a84a','#52a852','#4a9fd4','#7b62d4','#c462d4','#4ac8b4'];
const avatarColor = id => { let h=0; for (const c of String(id)) h=(h*31+c.charCodeAt(0))%PAL.length; return PAL[h]; };
const initials = n => (n||'U').replace(/[._]/g,' ').split(' ').map(w=>w[0]||'').join('').slice(0,2).toUpperCase();
const timeAgo = ts => { const m=(Date.now()-new Date(ts).getTime())/60000; if(m<1) return 'just now'; if(m<60) return `${Math.round(m)}m ago`; if(m<1440) return `${Math.floor(m/60)}h ago`; return `${Math.floor(m/1440)}d ago`; };
const formatDate = d => new Date(d+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'});

// Deterministic tie-breaker: uses debate ID hash so the same debate always
// breaks the same way — avoids flip-flopping between renders.
function tieBreak(debateId) {
  const h = (debateId||'').split('').reduce((a,c)=>((a<<5)+a)^c.charCodeAt(0), 0);
  return (h & 1) ? 52 : 48;
}

function calcPctA(debate, debateComments, userVote, voteCounts) {
  if (!debate) return 51;
  if (debate.is_closed) {
    const stored = debate.final_pct_a ?? null;
    if (stored === null || stored === 50) return tieBreak(debate.id);
    return stored;
  }
  const boostA = debateComments.filter(c => c.side==='A' && c.upvote_count>=5).length * 0.5;
  const boostB = debateComments.filter(c => c.side==='B' && c.upvote_count>=5).length * 0.5;
  const baseA = (voteCounts?.countA??0) + (debate.base_seed_a??45);
  const baseB = (voteCounts?.countB??0) + (debate.base_seed_b??45);
  const totalA = baseA + boostA + (userVote==='A'?1:0);
  const totalB = baseB + boostB + (userVote==='B'?1:0);
  const raw = Math.round((totalA/(totalA+totalB))*100);
  return raw === 50 ? tieBreak(debate.id) : raw;
}

// ─── PickASydeLogo ────────────────────────────────────────────────────────────
// Pick (teal) · A (white diamond shimmer, animated pivot) · Syde (red-orange)
function PickASydeLogo({ size = 'small' }) {
  const large = size === 'large';
  const fs    = large ? 'clamp(34px, 10vw, 72px)' : 28;
  const aFs   = large ? 'clamp(40px, 12vw, 86px)' : 34;   // A is noticeably bigger

  const textStyle = (color) => ({
    fontFamily: "'DM Sans',system-ui,sans-serif",
    fontSize: fs,
    fontWeight: 900,
    fontStyle: 'italic',
    color,
    textShadow: `0 0 ${large?28:10}px ${color}99, 0 2px 0 rgba(0,0,0,0.85)`,
    letterSpacing: large ? '-3px' : '-0.5px',
    display: 'inline-block',
    whiteSpace: 'nowrap',
  });

  return (
    <div className="pas-logo-wrap" style={{ whiteSpace: 'nowrap' }}>
      <div className="pas-logo">
        <span style={{ ...textStyle('#4fc4b8'), transform: 'skewX(-8deg)' }}>Pick</span>
        <span
          className="pas-a-diamond"
          style={{
            fontFamily: "'DM Sans',system-ui,sans-serif",
            fontSize: aFs,
            fontWeight: 900,
            fontStyle: 'italic',
            letterSpacing: 0,
            margin: large ? '0 2px' : '0 1px',
          }}
        >A</span>
        <span style={{ ...textStyle('#e8635a'), transform: 'skewX(-8deg)' }}>Syde</span>
      </div>
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
function Av({ uid, name, size = 32 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:avatarColor(uid||'anon'), display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.33, fontWeight:800, color:'#fff', fontFamily:'monospace', flexShrink:0 }}>
      {initials(name)}
    </div>
  );
}

// ─── Fake voter name pool (used to pad voter counts to ≥20) ───────────────────
const FAKE_VOTER_NAMES = [
  'Jordan M.','Alex T.','Taylor K.','Sam R.','Casey B.','Morgan L.',
  'Jamie P.','Drew S.','Quinn A.','Riley H.','Avery C.','Blake N.',
  'Charlie W.','Dakota F.','Elliot J.','Finley G.','Hayden O.','Jessie V.',
  'Kai U.','Logan D.','Micah Z.','Noah Y.','Oakley X.','Parker I.',
  'Reese Q.','Skyler E.','Tatum W.','Uriah S.','Valery T.','Winter R.',
  'Xander Q.','Yara P.','Zion N.','Adrian M.','Brody L.','Cameron K.',
  'Devon J.','Emery I.','Faye H.','Gage G.','Harper F.','Iris E.',
  'Jace D.','Kelsey C.','Luca B.','Maya A.','Nate Z.','Olive Y.',
  'Percy X.','Quinn W.','Roman V.','Sara U.','Tyler T.','Uma S.',
  'Vince R.','Wendy Q.','Xavier P.','Yvonne O.','Zack N.','Amber M.',
  'Brett L.','Chloe K.','Damon J.','Ellie I.','Franco H.','Gracie G.',
  'Hudson F.','Ivy E.','Joel D.','Kira C.','Leon B.','Mia A.',
  'Nick Z.','Opal Y.','Pedro X.','Rosa W.','Seth V.','Tina U.',
  'Ulric T.','Vera S.','Wade R.','Xena Q.','Yusuf P.','Zara O.',
];

function getPaddedVoters(realVoters, pctA, debateId) {
  const hash = hashStr(debateId || 'default');
  const sideA = realVoters.filter(v => v.side === 'A');
  const sideB = realVoters.filter(v => v.side === 'B');
  const realTotal = sideA.length + sideB.length;
  // Target between 20 and 31, always >= realTotal
  const target = Math.max(realTotal, 20 + (hash % 12));
  const deficit = target - realTotal;
  if (deficit <= 0) return { paddedA: sideA, paddedB: sideB };
  // Distribute fake names proportionally by pctA (more on winning side)
  const fakeA = Math.round(deficit * (pctA / 100));
  const fakeB = deficit - fakeA;
  const usedNames = new Set(realVoters.map(v => v.display_name));
  const pool = FAKE_VOTER_NAMES.filter(n => !usedNames.has(n));
  const pickNames = (count, offset) =>
    Array.from({ length: count }, (_, i) => ({
      display_name: pool[(hash + offset + i * 7) % pool.length],
      side: offset === 0 ? 'A' : 'B',
    }));
  return {
    paddedA: [...sideA, ...pickNames(fakeA, 0)],
    paddedB: [...sideB, ...pickNames(fakeB, 1)],
  };
}

// ─── VoteBar ──────────────────────────────────────────────────────────────────
function VoteBar({ pctA, lA, lB, voteCounts, mindsChanged, animate = true, debateId = '', onVoterClick }) {
  const [displayed, setDisplayed] = useState(animate ? 50 : pctA);
  useEffect(() => {
    if (animate) { const t = setTimeout(()=>setDisplayed(pctA), 300); return ()=>clearTimeout(t); }
    else setDisplayed(pctA);
  }, [pctA, animate]);

  // Use real vote counts when available, otherwise a stable per-debate seed
  const realTotal = voteCounts ? (voteCounts.countA ?? 0) + (voteCounts.countB ?? 0) : null;
  const hash = debateId ? hashStr(debateId) : 0;
  const fakeBase = debateId ? 75 + (hash % 26) : 88;
  const minVotes = 20 + (hash % 12); // minimum displayed: 20–31, stable per debate
  const totalVotes = realTotal !== null ? Math.max(minVotes, realTotal) : fakeBase;
  const formattedVotes = new Intl.NumberFormat('en-US').format(totalVotes);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
        <span style={{ fontWeight:800, fontSize:14, color:'#4fc4b8' }}>{Math.round(displayed)}% · {lA}</span>
        <span style={{ fontWeight:800, fontSize:14, color:'#e8635a' }}>{lB} · {100-Math.round(displayed)}%</span>
      </div>
      <div style={{ height:10, borderRadius:5, background:'#e8635a20', overflow:'hidden', position:'relative' }}>
        <div style={{ position:'absolute', left:0, top:0, bottom:0, width:`${displayed}%`, background:'linear-gradient(90deg,#4fc4b8,#38a89d)', borderRadius:'5px 0 0 5px', transition:'width 0.9s cubic-bezier(.4,0,.2,1)', boxShadow:'2px 0 10px rgba(79,196,184,0.5)' }} />
        <div style={{ position:'absolute', left:'50%', top:0, bottom:0, width:1, background:'rgba(255,255,255,0.07)', transform:'translateX(-50%)' }} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:10, fontSize:11, color:'#5070a0', fontWeight:600 }}>
        <span
          onClick={onVoterClick && debateId ? ()=>onVoterClick(debateId) : undefined}
          style={{ cursor: onVoterClick && debateId ? 'pointer' : 'default', textDecoration: onVoterClick && debateId ? 'underline dotted' : 'none' }}
        >🗳️ {formattedVotes} votes cast</span>
        {mindsChanged !== undefined && <span>🔄 {mindsChanged} minds changed today</span>}
      </div>
    </div>
  );
}

// ─── VoterNamesModal ──────────────────────────────────────────────────────────
// Returns "FirstName L." format — consistent for both real and fake voters
function formatDisplayName(name) {
  if (!name || name === 'user') return 'user';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return parts[0] + ' ' + parts[parts.length - 1][0].toUpperCase() + '.';
}

function VoterNamesModal({ debateId, lA, lB, pctA = 50, onClose }) {
  const [names, setNames] = useState(null);
  useEffect(() => {
    if (!debateId) return;
    getDebateVoterNames(debateId).then(setNames).catch(()=>setNames([]));
  }, [debateId]);

  const { paddedA, paddedB } = names
    ? getPaddedVoters(names, pctA, debateId)
    : { paddedA: [], paddedB: [] };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={onClose}>
      <div style={{ background:'#0e0e22', border:'1px solid #2a2a44', borderRadius:16, padding:20, maxWidth:400, width:'100%', maxHeight:'80vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ fontWeight:900, fontSize:15, color:'#d0d0e8' }}>🗳️ Who Voted</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#33334a', fontSize:18, cursor:'pointer', padding:0 }}>✕</button>
        </div>
        {!names && <div style={{ color:'#33334a', textAlign:'center', padding:20 }}>Loading…</div>}
        {names && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[['A',lA,paddedA,'#4fc4b8'],['B',lB,paddedB,'#e8635a']].map(([side,label,voters,col])=>(
              <div key={side}>
                <div style={{ fontSize:11, fontWeight:800, color:col, marginBottom:8, letterSpacing:0.5 }}>{label} · {voters.length}</div>
                {voters.length===0 && <div style={{ fontSize:11, color:'#33334a' }}>No votes yet</div>}
                {voters.map((v,i)=>(
                  <div key={i} style={{ fontSize:12, color:'#8888c0', padding:'3px 0', borderBottom:'1px solid #14142a' }}>{formatDisplayName(v.display_name)}</div>
                ))}
              </div>
            ))}
          </div>
        )}
        {names && <div style={{ marginTop:14, fontSize:11, color:'#33334a', textAlign:'center' }}>Total: {paddedA.length + paddedB.length} votes cast</div>}
      </div>
    </div>
  );
}

// ─── CommentCard ──────────────────────────────────────────────────────────────
function CommentCard({ c, currentUserId, hasUpvoted, onUpvote, onRemoveUpvote, locked, onEdit, onDelete, userVote, canSwitchSides, onChangedMyMind }) {
  const [editing, setEditing]       = useState(false);
  const [editText, setEditText]     = useState(c.text);
  const [saving, setSaving]         = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const displayName  = c.user_id===currentUserId ? 'You' : (c.user_profiles?.display_name||'user');
  const isOwn        = currentUserId===c.user_id;
  const isHistorical = c.comment_status === 'historical';
  // Can edit only if: own comment, not locked, and not historical (historical = read-only)
  const canEdit      = isOwn && !locked && !isHistorical;
  const boosting     = c.upvote_count >= 5;
  // "This changed my mind" shows on opposite-side comments only
  const isOppositeSide = userVote && c.side !== userVote && !isOwn;
  const showChangedMyMind = isOppositeSide && canSwitchSides && !locked;

  async function saveEdit() {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === c.text) { setEditing(false); setEditText(c.text); return; }
    setSaving(true);
    try { await onEdit(c.id, trimmed); setEditing(false); }
    catch(err) { console.error('edit error:', err); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ background:'#0e0e22', border:'1px solid #191930', borderLeft:`3px solid ${c.side==='A'?'#4fc4b8':'#e8635a'}`, borderRadius:'0 10px 10px 0', padding:'12px 14px', marginBottom:8 }}>
      <div style={{ display:'flex', gap:9, alignItems:'flex-start' }}>
        <Av uid={c.user_id} name={displayName} size={28} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5, flexWrap:'wrap' }}>
            <span style={{ fontWeight:700, fontSize:13, color:'#c8c8e0' }}>{displayName}</span>
            <span style={{ fontSize:10, fontWeight:800, padding:'1px 6px', borderRadius:3, letterSpacing:0.8, background:c.side==='A'?'rgba(79,196,184,.12)':'rgba(232,99,90,.12)', color:c.side==='A'?'#4fc4b8':'#e8635a', border:`1px solid ${c.side==='A'?'#4fc4b818':'#e8635a18'}` }}>
              {c.side_label||(c.side==='A'?'SIDE A':'SIDE B')}
            </span>
            {boosting && <span style={{ fontSize:10, fontWeight:800, color:'#f7c948' }}>⚡ BOOST</span>}
            <span style={{ fontSize:10, color:'#2e2e48', marginLeft:'auto' }}>{timeAgo(c.created_at)}</span>
            {canEdit && !editing && !confirmDel && (
              <button onClick={()=>{setEditing(true);setEditText(c.text);}} title="Edit comment" style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'#33334a', padding:'0 0 0 4px', lineHeight:1 }}>✏️</button>
            )}
            {canEdit && !editing && !confirmDel && (
              <button onClick={()=>setConfirmDel(true)} title="Delete comment" style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'#33334a', padding:'0 0 0 2px', lineHeight:1 }}>🗑️</button>
            )}
            {confirmDel && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:5, marginLeft:4 }}>
                <span style={{ fontSize:11, color:'#e8635a' }}>Delete?</span>
                <button onClick={()=>onDelete(c.id)} style={{ background:'#e8635a', border:'none', borderRadius:4, color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', cursor:'pointer' }}>Yes</button>
                <button onClick={()=>setConfirmDel(false)} style={{ background:'none', border:'1px solid #2a2a48', borderRadius:4, color:'#6060aa', fontSize:10, padding:'2px 7px', cursor:'pointer' }}>No</button>
              </span>
            )}
          </div>

          {editing ? (
            <div>
              <textarea
                value={editText}
                onChange={e=>setEditText(e.target.value)}
                rows={3}
                style={{ width:'100%', background:'#0a0a1a', border:'1px solid #2a2a48', borderRadius:8, color:'#d0d0e8', fontSize:16, padding:'9px 11px', resize:'none', boxSizing:'border-box', outline:'none', fontFamily:'inherit', lineHeight:1.55, marginBottom:8 }}
                autoFocus
              />
              <div style={{ display:'flex', gap:7 }}>
                <button onClick={saveEdit} disabled={saving} style={{ padding:'5px 13px', background:'linear-gradient(135deg,#4fc4b8,#38a89d)', border:'none', borderRadius:7, color:'#0a0a1a', fontWeight:800, fontSize:12, cursor:saving?'default':'pointer', opacity:saving?0.7:1 }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={()=>{setEditing(false);setEditText(c.text);}} style={{ padding:'5px 11px', background:'transparent', border:'1px solid #1e1e38', borderRadius:7, color:'#3a3a58', fontSize:12, cursor:'pointer' }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <p style={{ margin:'0 0 8px', fontSize:14, color:'#a8a8c8', lineHeight:1.6 }}>{stripHtml(c.text)}</p>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                {!locked && !isHistorical && (
                  <button
                    onClick={()=>{
                      if(isOwn) return;
                      if(hasUpvoted) onRemoveUpvote(c.id);
                      else onUpvote(c.id);
                    }}
                    title={hasUpvoted?'Remove upvote':'Upvote'}
                    style={{ display:'inline-flex', alignItems:'center', gap:4, background:hasUpvoted?'rgba(79,196,184,.15)':'rgba(255,255,255,.04)', border:`1px solid ${hasUpvoted?'#4fc4b8':'#1e1e38'}`, borderRadius:5, padding:'2px 9px', cursor:isOwn?'default':'pointer', fontSize:12, color:hasUpvoted?'#4fc4b8':'#3e3e58', fontWeight:600, transition:'all 0.15s' }}>
                    {hasUpvoted ? '▲' : '△'} {c.upvote_count}
                  </button>
                )}
                {(locked || isHistorical) && <span style={{ fontSize:12, color:'#2e2e48' }}>▲ {c.upvote_count}</span>}
                {showChangedMyMind && (
                  <button onClick={()=>onChangedMyMind(c)} style={{ marginLeft:'auto', display:'inline-flex', alignItems:'center', gap:6, background:'linear-gradient(135deg,rgba(247,201,72,0.12),rgba(232,99,90,0.07))', border:'1px solid rgba(247,201,72,0.35)', borderRadius:20, padding:'5px 14px', fontSize:12, fontWeight:900, color:'#f7c948', cursor:'pointer', letterSpacing:0.4, boxShadow:'0 2px 10px rgba(247,201,72,0.12)', flexShrink:0, transition:'all 0.15s' }}>
                    💡 This changed my mind
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── VoteFlashOverlay ─────────────────────────────────────────────────────────
function VoteFlashOverlay({ side, label, color, onDone }) {
  useEffect(() => { const t=setTimeout(onDone, 2600); return ()=>clearTimeout(t); }, [onDone]);

  const bolts    = Array.from({length:12},(_,i)=>{ const a=(i*30)*Math.PI/180, d=155+(i%4)*30; return {tx:Math.cos(a)*d,ty:Math.sin(a)*d,r:i*30,delay:i*0.05}; });
  const particles= Array.from({length:26},(_,i)=>{ const a=(i*13.85)*Math.PI/180, d=75+(i%5)*30; return {tx:Math.cos(a)*d,ty:Math.sin(a)*d,size:3+(i%5)*2.4,delay:(i%7)*0.04}; });

  return (
    <div className="vf-overlay vf-shake" style={{ position:'fixed', inset:0, zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
      <div className="vf-bg" style={{ position:'absolute', inset:0, background:`radial-gradient(ellipse at center, ${color}cc 0%, ${color}55 30%, ${color}11 60%, transparent 78%)` }} />
      <div className="vf-ring"  style={{ position:'absolute', left:'50%', top:'50%', width:90,  height:90,  borderRadius:'50%', border:`3px solid ${color}`, boxShadow:`0 0 24px ${color}` }} />
      <div className="vf-ring2" style={{ position:'absolute', left:'50%', top:'50%', width:55,  height:55,  borderRadius:'50%', border:`2px solid #f7c948`, boxShadow:`0 0 16px #f7c948` }} />
      {particles.map((p,i)=>(
        <div key={i} className="vf-particle" style={{ position:'absolute', left:'50%', top:'50%', width:p.size, height:p.size, borderRadius:'50%', background:i%3===0?'#f7c948':color, boxShadow:`0 0 6px ${i%3===0?'#f7c948':color}`, animationDelay:`${p.delay}s`, '--tx':`${p.tx}px`, '--ty':`${p.ty}px` }} />
      ))}
      {bolts.map((b,i)=>(
        <div key={i} className="vf-bolt" style={{ position:'absolute', left:'50%', top:'50%', fontSize:20, lineHeight:1, animationDelay:`${b.delay}s`, '--tx':`${b.tx}px`, '--ty':`${b.ty}px`, '--r':`${b.r}deg` }}>⚡</div>
      ))}
      {/* Main display */}
      <div style={{ position:'relative', zIndex:2, textAlign:'center' }}>
        <div className="vf-label" style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(12px,2.5vw,18px)', fontWeight:900, fontStyle:'italic', color:'#ffffff77', textShadow:`0 0 10px ${color}`, letterSpacing:'5px', textTransform:'uppercase', marginBottom:4 }}>
          You picked
        </div>
        <div className="vf-chosen" style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(38px,9vw,72px)', fontWeight:900, fontStyle:'italic', color:'#fff', textShadow:`0 0 28px ${color}, 0 0 60px ${color}, 0 4px 0 rgba(0,0,0,0.9)`, letterSpacing:'-2px', whiteSpace:'nowrap', WebkitTextStroke:`2px ${color}` }}>
          {label}
        </div>
        <div className="vf-label" style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(18px,4vw,32px)', fontWeight:900, fontStyle:'italic', color:'#f7c948', textShadow:'0 0 18px #f7c948, 0 0 45px rgba(247,201,72,0.4)', letterSpacing:'2px', marginTop:10, animationDelay:'0.12s' }}>
          ✓ VOTED!
        </div>
      </div>
    </div>
  );
}

// ─── SwitchConfirmModal ───────────────────────────────────────────────────────
function SwitchConfirmModal({ debate, previousSide, newSide, persuadingComment, onConfirm, onCancel, switching }) {
  const [reason, setReason] = useState('');
  const prevLabel = previousSide==='A' ? debate.label_a : debate.label_b;
  const newLabel  = newSide==='A' ? debate.label_a : debate.label_b;
  const prevColor = previousSide==='A' ? '#4fc4b8' : '#e8635a';
  const newColor  = newSide==='A' ? '#4fc4b8' : '#e8635a';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:16 }}>
      <div style={{ background:'#0e0e22', border:'1px solid #1e1e3a', borderRadius:20, padding:24, maxWidth:380, width:'100%' }}>
        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:18 }}>
          <div style={{ fontSize:22, marginBottom:8 }}>🔄</div>
          <h3 style={{ margin:0, fontWeight:900, fontSize:17, color:'#eeeeff' }}>Switch your vote?</h3>
          <p style={{ margin:'8px 0 0', fontSize:13, color:'#5a5a80' }}>
            This will switch your vote from{' '}
            <span style={{ color:prevColor, fontWeight:800 }}>{prevLabel}</span>
            {' '}to{' '}
            <span style={{ color:newColor, fontWeight:800 }}>{newLabel}</span>.
          </p>
        </div>

        {/* The persuading comment */}
        {persuadingComment && (
          <div style={{ background:'#080818', border:'1px solid #252545', borderLeft:`3px solid ${newColor}`, borderRadius:'0 8px 8px 0', padding:'10px 12px', marginBottom:16, fontSize:13, color:'#7070a8', fontStyle:'italic', lineHeight:1.5 }}>
            "{persuadingComment.text.length>140 ? persuadingComment.text.slice(0,140)+'…' : persuadingComment.text}"
            <div style={{ fontSize:11, color:'#3a3a58', marginTop:5, fontStyle:'normal' }}>— {persuadingComment.user_profiles?.display_name||'user'}</div>
          </div>
        )}

        {/* Optional reason */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', fontSize:12, color:'#4a4a70', marginBottom:7, fontWeight:700 }}>What changed your mind? <span style={{ fontWeight:400, color:'#33334a' }}>(optional)</span></label>
          <textarea
            value={reason}
            onChange={e=>setReason(e.target.value.slice(0,280))}
            placeholder="Explain your new position…"
            rows={3}
            style={{ width:'100%', background:'#0a0a1a', border:'1px solid #1e1e38', borderRadius:9, color:'#d0d0e8', fontSize:16, padding:'10px 12px', resize:'none', boxSizing:'border-box', outline:'none', fontFamily:'inherit', lineHeight:1.5 }}
          />
          <div style={{ textAlign:'right', fontSize:11, color:'#2e2e48', marginTop:3 }}>{reason.length}/280</div>
        </div>

        {/* Note about old comment */}
        <p style={{ fontSize:11, color:'#3a3a58', margin:'0 0 16px', lineHeight:1.5 }}>
          Your previous comment will remain visible as part of the debate history.
        </p>

        {/* Buttons */}
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel} disabled={switching} style={{ flex:1, padding:'11px 0', background:'transparent', border:'1px solid #1e1e38', borderRadius:10, color:'#5a5a80', fontWeight:700, fontSize:14, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={()=>onConfirm(reason.trim())} disabled={switching} style={{ flex:1, padding:'11px 0', background:`linear-gradient(135deg,${newColor},${newColor}cc)`, border:'none', borderRadius:10, color:'#0a0a1a', fontWeight:900, fontSize:14, cursor:switching?'default':'pointer', opacity:switching?0.7:1 }}>
            {switching ? 'Switching…' : `Switch to ${newLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AnimatedQuestions ────────────────────────────────────────────────────────
function AnimatedQuestions({ compact = false }) {
  const [idx, setIdx] = useState(0);
  const [key, setKey] = useState(0);
  useEffect(() => { const t=setInterval(()=>{setIdx(i=>(i+1)%SAMPLE_QUESTIONS.length);setKey(k=>k+1);},2800); return()=>clearInterval(t); },[]);
  const shown = Array.from({length:5},(_,i)=>SAMPLE_QUESTIONS[(idx+i)%SAMPLE_QUESTIONS.length]);
  const opacities = [0.5, 0.72, 1, 0.72, 0.5];
  const sizes     = [11,   13,  16, 13,   11  ];
  const weights   = [400,  600, 800, 600,  400 ];
  const colors    = ['#3a3a72','#5858a8','#a8a8e8','#5858a8','#3a3a72'];
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', padding: compact ? '24px 20px' : '40px 28px 40px 24px' }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:3, color:'#6060aa', marginBottom:24, textTransform:'uppercase' }}>Pick A Syde on…</div>
      <div style={{ display:'flex', flexDirection:'column', gap: compact ? 12 : 18 }}>
        {shown.map((text,i)=>(
          <div key={`${key}-${i}`} style={{ fontSize:sizes[i], fontWeight:weights[i], color:colors[i], opacity:opacities[i], lineHeight:1.4, paddingLeft:i===2?12:0, borderLeft:i===2?'2px solid #8888dd':'2px solid transparent', animation:i===4?'aqFade 0.5s ease-out':undefined }}>
            {text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PlatformExplainer ────────────────────────────────────────────────────────
function PlatformExplainer({ compact = false }) {
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', padding: compact ? '24px 20px' : '40px 24px 40px 28px' }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:3, color:'#6060aa', marginBottom:16, textTransform:'uppercase' }}>What is PickASyde?</div>
      <p style={{ fontSize:16, fontWeight:800, color:'#a0a0e0', lineHeight:1.55, margin:'0 0 14px' }}>
        One question.<br/>Two sides.<br/>You decide.
      </p>
      <p style={{ fontSize:13, color:'#8080c0', lineHeight:1.85, margin:'0 0 12px' }}>
        Every day a new debate drops. Pick your side, back it with a comment, and watch the argument play out live.
      </p>
      <p style={{ fontSize:12, color:'#6868a8', lineHeight:1.85, margin:'0 0 20px' }}>
        No doom-scrolling. No algorithm rabbit holes. One focused debate per day — then it locks at midnight and the result stands forever.
      </p>
      <div style={{ borderTop:'1px solid #252545', paddingTop:16, display:'flex', flexDirection:'column', gap:10 }}>
        {[
          ['⚡','Votes update live as the crowd picks sides'],
          ['💬','5 upvotes on your comment boosts your side\'s score'],
          ['🔥','Vote every day to build your streak'],
          ['🔒','Debate locks at midnight EST — the verdict is final'],
          ['😌','Totally free, no ads chasing you around'],
        ].map(([ic,text])=>(
          <div key={text} style={{ display:'flex', alignItems:'flex-start', gap:9, fontSize:12, color:'#7070b0', lineHeight:1.5 }}>
            <span style={{ flexShrink:0, marginTop:1 }}>{ic}</span><span>{text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── IG Story Image Generator ─────────────────────────────────────────────────
function buildShareCanvas(debate, vote, pct) {
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  const sideColor = vote === 'A' ? '#4fc4b8' : '#e8635a';
  const sideLabel = vote === 'A' ? debate.label_a : debate.label_b;
  const otherLabel = vote === 'A' ? debate.label_b : debate.label_a;
  const otherPct = 100 - pct;

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#07071a'); bg.addColorStop(1, '#100e28');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // Subtle radial glow
  const glow = ctx.createRadialGradient(W/2, H*0.35, 0, W/2, H*0.35, W*0.7);
  glow.addColorStop(0, sideColor + '18'); glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  // Top accent bar
  ctx.fillStyle = sideColor; ctx.fillRect(0, 0, W, 8);

  // Brand header
  ctx.fillStyle = '#ffffff18';
  ctx.fillRect(W/2 - 160, 60, 320, 70);
  ctx.strokeStyle = sideColor + '44'; ctx.lineWidth = 1.5;
  ctx.strokeRect(W/2 - 160, 60, 320, 70);
  ctx.fillStyle = '#d0d0ff'; ctx.font = 'bold 42px sans-serif';
  ctx.textAlign = 'center'; ctx.fillText('⚡ PICKASYDE', W/2, 106);

  // Question box
  const qY = 220;
  ctx.fillStyle = '#ffffff08'; ctx.beginPath();
  ctx.roundRect(80, qY, W - 160, 340, 24);
  ctx.fill();
  ctx.strokeStyle = sideColor + '33'; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.roundRect(80, qY, W - 160, 340, 24); ctx.stroke();

  // Question text (word wrapped)
  ctx.fillStyle = '#eeeeff'; ctx.font = 'bold 72px sans-serif';
  ctx.textAlign = 'center';
  const words = debate.question.split(' ');
  const maxW = W - 200; let lines = [], line = '';
  words.forEach(w => {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxW) { lines.push(line); line = w; }
    else line = test;
  });
  if (line) lines.push(line);
  const lineH = 84, totalH = lines.length * lineH;
  const startY = qY + 170 - totalH / 2;
  lines.forEach((l, i) => ctx.fillText(l, W/2, startY + i * lineH));

  // "MY TAKE" label
  ctx.fillStyle = '#55557a'; ctx.font = 'bold 36px sans-serif';
  ctx.fillText('MY TAKE', W/2, qY + 380);

  // Side chip
  const chipY = qY + 430;
  ctx.fillStyle = sideColor + '25';
  ctx.beginPath(); ctx.roundRect(W/2 - 280, chipY, 560, 110, 55); ctx.fill();
  ctx.strokeStyle = sideColor + '88'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.roundRect(W/2 - 280, chipY, 560, 110, 55); ctx.stroke();
  ctx.fillStyle = sideColor; ctx.font = 'bold 70px sans-serif';
  ctx.fillText(`✓  ${sideLabel}`, W/2, chipY + 74);

  // Vote bar background
  const barY = qY + 600, barH = 36;
  ctx.fillStyle = '#ffffff10'; ctx.beginPath();
  ctx.roundRect(80, barY, W - 160, barH, barH/2); ctx.fill();
  // Filled portion
  const barFillW = Math.max(40, Math.min(W - 160, (W - 160) * (pct / 100)));
  const barGrad = ctx.createLinearGradient(80, 0, 80 + barFillW, 0);
  barGrad.addColorStop(0, sideColor); barGrad.addColorStop(1, sideColor + 'aa');
  ctx.fillStyle = barGrad; ctx.beginPath();
  ctx.roundRect(80, barY, barFillW, barH, barH/2); ctx.fill();

  // Pct labels
  ctx.font = 'bold 52px sans-serif'; ctx.textAlign = 'left';
  ctx.fillStyle = sideColor; ctx.fillText(`${pct}%`, 80, barY + 90);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#44446a'; ctx.fillText(`${otherPct}%`, W - 80, barY + 90);

  // Side labels small
  ctx.font = '38px sans-serif'; ctx.textAlign = 'left';
  ctx.fillStyle = sideColor + 'cc'; ctx.fillText(sideLabel, 80, barY + 145);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#33334a'; ctx.fillText(otherLabel, W - 80, barY + 145);

  // CTA
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff10'; ctx.beginPath();
  ctx.roundRect(80, H - 360, W - 160, 140, 20); ctx.fill();
  ctx.fillStyle = '#8888cc'; ctx.font = 'bold 48px sans-serif';
  ctx.fillText('Cast your vote at', W/2, H - 285);
  ctx.fillStyle = '#d0d0ff'; ctx.font = 'bold 68px sans-serif';
  ctx.fillText('pickasyde.com', W/2, H - 210);

  // Bottom accent
  ctx.fillStyle = sideColor; ctx.fillRect(0, H - 8, W, 8);

  return canvas;
}

// ─── Share Modal ──────────────────────────────────────────────────────────────
function ShareModal({ debate, vote, commentText, pct, streak, onClose }) {
  const [copied, setCopied] = useState(false);
  const [igLoading, setIgLoading] = useState(false);
  const [igSaved, setIgSaved] = useState(false);
  const [ttCopied, setTtCopied] = useState(false);
  const shareText = generateShareText(debate, vote, commentText, pct);
  const sideLabel = vote==='A' ? debate.label_a : debate.label_b;
  const sideColor = vote==='A' ? '#4fc4b8' : '#e8635a';
  const otherPct  = 100 - pct;
  const winning   = pct >= 50;
  const copyText  = ()=> navigator.clipboard.writeText(shareText).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2500);});
  const shareX    = ()=> window.open(getXShareUrl(shareText),'_blank');
  const shareTikTok = ()=> navigator.clipboard.writeText(shareText).then(()=>{setTtCopied(true);setTimeout(()=>setTtCopied(false),2500);});

  const shareIG = async () => {
    if (igSaved) {
      // Second tap: open Instagram
      window.open('instagram://', '_blank');
      return;
    }
    setIgLoading(true);
    try {
      const canvas = buildShareCanvas(debate, vote, pct);
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      // Always save to camera roll / downloads — avoids IG sticker mode
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'pickasyde-story.png'; a.click();
      URL.revokeObjectURL(url);
      setIgSaved(true);
    } catch(err) {
      console.error('IG share error:', err);
    } finally { setIgLoading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.93)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:16 }}>
      <div style={{ background:'#0a0a1c', border:'1px solid #1e1e3a', borderRadius:24, padding:24, maxWidth:380, width:'100%', boxShadow:'0 24px 80px rgba(0,0,0,.8)' }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
          <div>
            <h3 style={{ margin:0, fontWeight:900, fontSize:20, color:'#eeeeff', letterSpacing:-0.3 }}>Prove your point.</h3>
            <p style={{ margin:'4px 0 0', fontSize:12, color:'#44446a' }}>
              {winning
                ? `Your side is winning — keep the momentum going 🔥`
                : `Your side is down ${otherPct - pct}% — recruit some allies 📣`}
            </p>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,.05)', border:'1px solid #1e1e3a', color:'#55557a', cursor:'pointer', fontSize:14, borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:12 }}>✕</button>
        </div>

        {/* Preview card */}
        <div style={{ background:'linear-gradient(135deg,#0d0d20,#141428)', border:`1px solid ${sideColor}22`, borderRadius:16, padding:16, margin:'16px 0', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,${sideColor},transparent)` }}/>
          <div style={{ fontSize:9, fontWeight:800, letterSpacing:2.5, color:'#33334a', marginBottom:8 }}>⚡ PICKASYDE · {formatDate(debate.date)}</div>
          <div style={{ fontSize:13, fontWeight:700, color:'#c0c0dc', marginBottom:10, lineHeight:1.4 }}>"{debate.question}"</div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:`${sideColor}15`, border:`1px solid ${sideColor}30`, borderRadius:20, padding:'4px 12px', marginBottom:commentText?8:12 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:sideColor, display:'inline-block' }}/>
            <span style={{ fontSize:12, fontWeight:800, color:sideColor }}>Team {sideLabel}</span>
          </div>
          {commentText && <p style={{ margin:'0 0 10px', fontSize:11, color:'#6060a0', fontStyle:'italic', lineHeight:1.5, borderLeft:`2px solid ${sideColor}40`, paddingLeft:8 }}>"{commentText.slice(0,80)}{commentText.length>80?'…':''}"</p>}
          <div style={{ height:4, borderRadius:2, background:'#ffffff08', overflow:'hidden', marginBottom:5 }}>
            <div style={{ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg,${sideColor},${sideColor}aa)`, borderRadius:2, transition:'width 0.6s ease' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, fontWeight:700, marginBottom:0 }}>
            <span style={{ color:sideColor }}>{pct}% with me</span>
            <span style={{ color:'#33334a' }}>pickasyde.com</span>
          </div>
        </div>

        {/* Streak nudge */}
        {streak>=2 && (
          <div style={{ background:'rgba(247,201,72,.06)', border:'1px solid rgba(247,201,72,.15)', borderRadius:12, padding:'10px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>🔥</span>
            <div>
              <div style={{ fontSize:11, fontWeight:900, color:'#f7c948', letterSpacing:0.5 }}>{streak}-DAY STREAK</div>
              <div style={{ fontSize:11, color:'#7777aa', marginTop:1 }}>Challenge a friend to keep up with you.</div>
            </div>
          </div>
        )}

        {/* Social buttons */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:10 }}>
          {/* X / Twitter */}
          <button onClick={shareX} style={{ padding:'13px 6px', background:'#000', border:'1px solid #2a2a2a', borderRadius:12, color:'#fff', fontWeight:800, fontSize:12, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:5, transition:'border-color 0.15s', lineHeight:1 }}
            onMouseEnter={e=>e.currentTarget.style.borderColor='#444'} onMouseLeave={e=>e.currentTarget.style.borderColor='#2a2a2a'}>
            <span style={{ fontSize:16, fontWeight:900 }}>𝕏</span>
            <span>Post now</span>
          </button>

          {/* Instagram */}
          <button onClick={shareIG} disabled={igLoading} style={{ padding:'13px 6px', background: igLoading ? 'rgba(255,255,255,.06)' : igSaved ? 'rgba(79,196,184,.15)' : 'linear-gradient(145deg,#6a11cb,#fc5c7d)', border: igSaved ? '1px solid #4fc4b8' : 'none', borderRadius:12, color:igSaved?'#4fc4b8':'#fff', fontWeight:800, fontSize:12, cursor:igLoading?'default':'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:5, lineHeight:1, transition:'all 0.2s', opacity:igLoading?0.7:1 }}>
            {igLoading ? (
              <><span style={{ fontSize:14 }}>⏳</span><span>Saving…</span></>
            ) : igSaved ? (
              <><span style={{ fontSize:14 }}>✓</span><span>Open IG</span></>
            ) : (
              <><svg viewBox="0 0 24 24" width="18" height="18"><defs><radialGradient id="ig2" cx="30%" cy="107%" r="150%"><stop offset="0%" stopColor="#fdf497"/><stop offset="45%" stopColor="#fd5949"/><stop offset="60%" stopColor="#d6249f"/><stop offset="90%" stopColor="#285AEB"/></radialGradient></defs><rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="url(#ig2)"/><circle cx="12" cy="12" r="4.5" fill="none" stroke="white" strokeWidth="1.8"/><circle cx="17.5" cy="6.5" r="1.2" fill="white"/></svg><span>Instagram</span></>
            )}
          </button>

          {/* TikTok */}
          <button onClick={shareTikTok} style={{ padding:'13px 6px', background: ttCopied ? 'rgba(79,196,184,.1)' : '#0a0a0a', border: ttCopied ? '1px solid #4fc4b8' : '1px solid #2a2a2a', borderRadius:12, color:'#fff', fontWeight:800, fontSize:12, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:5, lineHeight:1, transition:'all 0.2s' }}
            onMouseEnter={e=>{if(!ttCopied)e.currentTarget.style.borderColor='#444';}} onMouseLeave={e=>{if(!ttCopied)e.currentTarget.style.borderColor='#2a2a2a';}}>
            {ttCopied ? (
              <><span style={{ fontSize:14 }}>✓</span><span style={{ color:'#4fc4b8' }}>Copied!</span></>
            ) : (
              <><svg viewBox="0 0 24 24" width="18" height="18" fill="white"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/></svg><span>TikTok</span></>
            )}
          </button>
        </div>

        {/* Instagram hint */}
        {igSaved && (
          <div style={{ background:'rgba(214,36,159,.08)', border:'1px solid rgba(214,36,159,.25)', borderRadius:10, padding:'9px 12px', marginBottom:10, fontSize:11, color:'#e060c0', textAlign:'center', lineHeight:1.6 }}>
            📸 Image saved! Open Instagram → Stories → tap the gallery icon → select your PickASyde image
          </div>
        )}

        {/* TikTok hint */}
        {ttCopied && (
          <div style={{ background:'rgba(79,196,184,.08)', border:'1px solid rgba(79,196,184,.2)', borderRadius:10, padding:'9px 12px', marginBottom:10, fontSize:11, color:'#4fc4b8', textAlign:'center' }}>
            ✓ Text copied — paste into your TikTok bio or caption
          </div>
        )}

        {/* Copy link */}
        <button onClick={copyText} style={{ width:'100%', padding:'12px 0', background:copied?'rgba(79,196,184,.1)':'rgba(255,255,255,.03)', border:`1px solid ${copied?'rgba(79,196,184,.4)':'#1e1e3a'}`, borderRadius:12, color:copied?'#4fc4b8':'#44447a', fontWeight:700, fontSize:12, cursor:'pointer', transition:'all 0.2s', letterSpacing:0.3 }}>
          {copied ? '✓ Copied to clipboard!' : '📋 Copy share text'}
        </button>
      </div>
    </div>
  );
}

// ─── Backoffice Screen ────────────────────────────────────────────────────────
function BackofficeScreen() {
  const [tab, setTab]           = useState('questions');
  const [debates, setDebates]   = useState([]);
  const [allComments, setAllComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // New debate form
  const [newQ, setNewQ]         = useState('');
  const [newDate, setNewDate]   = useState('');
  const [newLabelA, setNewLabelA] = useState('');
  const [newLabelB, setNewLabelB] = useState('');
  const [newImgA, setNewImgA]   = useState('');
  const [newImgB, setNewImgB]   = useState('');
  // Edit debate
  const [editId, setEditId]     = useState(null);
  const [editFields, setEditFields] = useState({});
  // Drag-to-reorder
  const [dragId, setDragId]         = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [swapping, setSwapping]     = useState(false);
  // Email tab
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [emailStatus, setEmailStatus]     = useState(null); // null | 'sending' | 'ok' | 'err'
  const [emailError, setEmailError]       = useState('');
  const [emailSubject, setEmailSubject]   = useState('');
  const [emailIntro, setEmailIntro]       = useState("Drop your take in the comments. Change someone's mind. 🔥");
  const [emailCta, setEmailCta]           = useState('Cast Your Vote →');
  const [emailPreviewHtml, setEmailPreviewHtml]     = useState('');
  const [emailPreviewOpen, setEmailPreviewOpen]     = useState(false);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewDate, setEmailPreviewDate]     = useState(''); // YYYY-MM-DD
  const [skipDates, setSkipDates]                   = useState([]);   // YYYY-MM-DD strings
  const [skipDatesLoading, setSkipDatesLoading]     = useState(false);
  // Settings tab state
  const [settingCommentReq, setSettingCommentReq] = useState(false);
  const [settingSaving, setSettingSaving]         = useState(false);
  const [settingSaveMsg, setSettingSaveMsg]       = useState('');
  // Comment tab state
  const [commentSort, setCommentSort]     = useState('newest');
  const [commentFilter, setCommentFilter] = useState('all'); // 'all' | 'A' | 'B' | 'ai' | 'human'
  const [editCommentId, setEditCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  // Voters tab state
  const [voters, setVoters]                     = useState([]);
  const [voterSearch, setVoterSearch]           = useState('');
  const [selectedVoters, setSelectedVoters]     = useState(new Set());
  const [expandedVoterId, setExpandedVoterId]   = useState(null);
  const [voterHistories, setVoterHistories]     = useState({}); // userId → history[]
  const [historyLoading, setHistoryLoading]     = useState(null); // userId being loaded
  const [customEmails, setCustomEmails]         = useState('');
  const [bulkEmailStatus, setBulkEmailStatus]   = useState(null); // null | 'sending' | 'ok' | 'err'
  const [bulkEmailError, setBulkEmailError]     = useState('');
  const [voterEmailSubject, setVoterEmailSubject] = useState('');
  const [voterEmailIntro, setVoterEmailIntro]   = useState("Drop your take in the comments. Change someone's mind. 🔥");
  const [voterEmailCta, setVoterEmailCta]       = useState('Cast Your Vote →');

  const inp = { width:'100%', padding:'9px 12px', background:'#0a0a1a', border:'1px solid #1e1e38', borderRadius:8, color:'#d0d0e8', fontSize:14, marginBottom:8, boxSizing:'border-box', outline:'none', fontFamily:'inherit' };

  useEffect(()=>{ loadData(); },[tab]);

  async function loadData() {
    setLoading(true);
    try {
      if(tab==='questions') {
        const data = await getAllDebatesAdmin();
        setDebates(data);
      } else if(tab==='comments') {
        const data = await getRecentCommentsAdmin(80);
        setAllComments(data);
      } else if(tab==='users') {
        const data = await getVoterEmails();
        setVoters(data);
      } else if(tab==='email') {
        loadSkipDates();
      } else if(tab==='settings') {
        const val = await getAppSetting('comment_required');
        setSettingCommentReq(val === 'true');
      }
    } catch(err){ console.error(err); }
    finally{ setLoading(false); }
  }

  async function handleCreate() {
    if(!newQ.trim()||!newDate||!newLabelA.trim()||!newLabelB.trim()) { alert('Fill in all fields'); return; }
    setSubmitting(true);
    try {
      await createDebate({ question:newQ.trim(), date:newDate, label_a:newLabelA.trim(), label_b:newLabelB.trim(), image_url_a:newImgA.trim()||null, image_url_b:newImgB.trim()||null });
      setNewQ(''); setNewDate(''); setNewLabelA(''); setNewLabelB(''); setNewImgA(''); setNewImgB('');
      loadData();
    } catch(err){ alert('Error: '+err.message); }
    finally{ setSubmitting(false); }
  }

  async function handleDeleteDebateRow(id) {
    if(!window.confirm('Delete this debate and all its votes/comments? This cannot be undone.')) return;
    try { await deleteDebate(id); loadData(); }
    catch(err){ alert('Error: '+err.message); }
  }

  async function handleSaveEdit(id) {
    try {
      await updateDebate(id, editFields);
      setEditId(null); setEditFields({});
      loadData();
    } catch(err){ alert('Error: '+err.message); }
  }

  async function handleDeleteCommentRow(id) {
    try {
      await deleteComment(id);
      setAllComments(prev=>prev.filter(c=>c.id!==id));
    } catch(err){ alert('Error: '+err.message); }
  }

  async function handleSaveCommentEdit(id) {
    const trimmed = editCommentText.trim();
    if (!trimmed) return;
    try {
      await updateComment(id, trimmed);
      setAllComments(prev => prev.map(c => c.id === id ? { ...c, text: trimmed } : c));
      setEditCommentId(null); setEditCommentText('');
    } catch(err) { alert('Error saving comment: ' + err.message); }
  }

  async function handleSwapDates(idA, dateA, idB, dateB) {
    if(idA===idB || swapping) return;
    setSwapping(true);
    try {
      // Use a temp sentinel date to avoid unique constraint collision
      await updateDebate(idA, { date: '2099-12-31' });
      await updateDebate(idB, { date: dateA });
      await updateDebate(idA, { date: dateB });
      await loadData();
    } catch(err){ alert('Swap failed: '+err.message); }
    finally{ setSwapping(false); setDragId(null); setDragOverId(null); }
  }

  async function handleSendTestEmail() {
    if (!testEmailAddr.trim()) { alert('Enter an email address'); return; }
    setEmailStatus('sending'); setEmailError('');
    try {
      const body = { targetEmail: testEmailAddr.trim() };
      if (emailSubject.trim()) body.customSubject = emailSubject.trim();
      if (emailIntro.trim())   body.customIntro   = emailIntro.trim();
      if (emailCta.trim())     body.customCta     = emailCta.trim();
      const { error } = await supabase.functions.invoke('daily-email', { body });
      if (error) throw error;
      setEmailStatus('ok');
    } catch(err) {
      setEmailStatus('err'); setEmailError(err.message || 'Unknown error');
    }
  }

  async function loadSkipDates() {
    try {
      const val = await getAppSetting('skip_email_dates');
      if (val) { try { setSkipDates(JSON.parse(val)); } catch(_) { setSkipDates([]); } }
      else setSkipDates([]);
    } catch(_) { setSkipDates([]); }
  }

  async function toggleSkipDate(dateStr) {
    setSkipDatesLoading(true);
    try {
      const next = skipDates.includes(dateStr)
        ? skipDates.filter(d => d !== dateStr)
        : [...skipDates, dateStr];
      await setAppSetting('skip_email_dates', JSON.stringify(next));
      setSkipDates(next);
    } catch(e) { console.error('toggleSkipDate error:', e); }
    setSkipDatesLoading(false);
  }

  async function handlePreviewEmail() {
    setEmailPreviewLoading(true);
    try {
      const body = { previewOnly: true };
      if (emailPreviewDate) body.targetDate = emailPreviewDate;
      if (emailSubject.trim()) body.customSubject = emailSubject.trim();
      if (emailIntro.trim())   body.customIntro   = emailIntro.trim();
      if (emailCta.trim())     body.customCta     = emailCta.trim();
      const { data, error } = await supabase.functions.invoke('daily-email', { body });
      if (error) throw error;
      if (data?.html) {
        setEmailPreviewHtml(data.html);
        setEmailPreviewOpen(true);
      } else {
        alert(data?.message || data?.error || 'No debate found for that date — check that it\'s been scheduled.');
      }
    } catch(e) {
      alert('Preview failed: ' + (e.message || String(e)));
    }
    setEmailPreviewLoading(false);
  }

  const today = new Date().toLocaleDateString('en-CA',{timeZone:'America/New_York'});

  return (
    <>
    <div style={{ maxWidth:680, margin:'0 auto', padding:'16px 14px 80px' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
        <div style={{ fontSize:20 }}>🛠️</div>
        <div>
          <div style={{ fontWeight:900, fontSize:18, color:'#d0d0e8' }}>Backoffice</div>
          <div style={{ fontSize:11, color:'#33334a' }}>Admin only — manage debates &amp; comments</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:20, borderBottom:'1px solid #1a1a30', paddingBottom:12, flexWrap:'wrap' }}>
        {[['questions','📅 Questions'],['comments','💬 Comments'],['users','👥 Voters'],['email','📧 Email'],['settings','⚙️ Settings']].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{ padding:'6px 16px', background:tab===k?'#1a1a35':'transparent', border:`1px solid ${tab===k?'#2a2a55':'#1a1a30'}`, borderRadius:20, color:tab===k?'#d0d0e8':'#33334a', fontWeight:tab===k?800:500, fontSize:13, cursor:'pointer' }}>{l}</button>
        ))}
      </div>

      {/* ── Questions Tab ── */}
      {tab==='questions' && (
        <div>
          {/* Add new debate form */}
          <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:18, marginBottom:20 }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#8888c8', marginBottom:14 }}>➕ Schedule a Debate</div>
            <input style={inp} placeholder="Debate question (e.g. Remote work or office?)" value={newQ} onChange={e=>setNewQ(e.target.value)}/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
              <input style={{...inp,marginBottom:0}} type="date" value={newDate} onChange={e=>setNewDate(e.target.value)}/>
              <input style={{...inp,marginBottom:0}} placeholder="Side A label" value={newLabelA} onChange={e=>setNewLabelA(e.target.value)}/>
              <input style={{...inp,marginBottom:0}} placeholder="Side B label" value={newLabelB} onChange={e=>setNewLabelB(e.target.value)}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
              <input style={{...inp,marginBottom:0}} placeholder="Side A image URL (optional)" value={newImgA} onChange={e=>setNewImgA(e.target.value)}/>
              <input style={{...inp,marginBottom:0}} placeholder="Side B image URL (optional)" value={newImgB} onChange={e=>setNewImgB(e.target.value)}/>
            </div>
            <button onClick={handleCreate} disabled={submitting} style={{ marginTop:8, padding:'9px 20px', background:'linear-gradient(135deg,#4fc4b8,#38a89d)', border:'none', borderRadius:9, color:'#0a0a1a', fontWeight:900, fontSize:13, cursor:submitting?'default':'pointer', opacity:submitting?0.7:1 }}>
              {submitting?'Saving…':'Add to Queue'}
            </button>
          </div>

          {/* Drag hint */}
          {debates.some(d=>d.date>today) && (
            <div style={{ fontSize:11, color:'#33334a', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:14 }}>⠿</span> Drag upcoming debates to swap their scheduled dates.
              {swapping && <span style={{ color:'#f7c948', marginLeft:8 }}>Swapping…</span>}
            </div>
          )}
          {/* Debate list */}
          {loading ? <div style={{ color:'#33334a', textAlign:'center', padding:32 }}>Loading…</div> : debates.map(d=>{
            const isPast   = d.date < today;
            const isToday  = d.date === today;
            const isFuture = d.date > today;
            const canDrag  = isFuture && !swapping;
            const isDragOver = dragOverId===d.id && dragId!==d.id;
            const statusColor = d.is_closed?'#e8635a55':isToday?'#4fc4b8':isFuture?'#f7c948':'#33334a';
            const statusLabel = d.is_closed?'🔒 Closed':isToday?'🔴 Live':isFuture?'📅 Upcoming':'⌛ Past';
            return (
              <div
                key={d.id}
                draggable={canDrag}
                onDragStart={canDrag ? ()=>setDragId(d.id) : undefined}
                onDragEnd={canDrag ? ()=>{setDragId(null);setDragOverId(null);} : undefined}
                onDragOver={canDrag ? e=>{e.preventDefault();setDragOverId(d.id);} : undefined}
                onDragLeave={canDrag ? ()=>{if(dragOverId===d.id)setDragOverId(null);} : undefined}
                onDrop={canDrag ? e=>{
                  e.preventDefault();
                  if(dragId && dragId!==d.id) {
                    const src = debates.find(x=>x.id===dragId);
                    if(src) handleSwapDates(src.id, src.date, d.id, d.date);
                  }
                  setDragOverId(null);
                } : undefined}
                style={{ background: isDragOver?'#141430':'#0e0e22', border: isDragOver?'1px solid #4fc4b855':'1px solid #191930', borderRadius:12, padding:'12px 14px', marginBottom:8, transition:'background 0.15s, border-color 0.15s', opacity: dragId===d.id?0.5:1, cursor: canDrag?'grab':'default' }}
              >
                {editId===d.id ? (
                  <div>
                    <input style={inp} value={editFields.question??d.question} onChange={e=>setEditFields(f=>({...f,question:e.target.value}))} placeholder="Question"/>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                      <input style={{...inp,marginBottom:0}} type="date" value={editFields.date??d.date} onChange={e=>setEditFields(f=>({...f,date:e.target.value}))}/>
                      <input style={{...inp,marginBottom:0}} placeholder="Side A" value={editFields.label_a??d.label_a} onChange={e=>setEditFields(f=>({...f,label_a:e.target.value}))}/>
                      <input style={{...inp,marginBottom:0}} placeholder="Side B" value={editFields.label_b??d.label_b} onChange={e=>setEditFields(f=>({...f,label_b:e.target.value}))}/>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop:8 }}>
                      <input style={{...inp,marginBottom:0}} placeholder="Side A image URL" value={editFields.image_url_a??d.image_url_a??''} onChange={e=>setEditFields(f=>({...f,image_url_a:e.target.value||null}))}/>
                      <input style={{...inp,marginBottom:0}} placeholder="Side B image URL" value={editFields.image_url_b??d.image_url_b??''} onChange={e=>setEditFields(f=>({...f,image_url_b:e.target.value||null}))}/>
                    </div>
                    {/* Result overrides — show for past/closed debates */}
                    {(isPast || d.is_closed) && (
                      <div style={{ marginTop:10, padding:'10px 12px', background:'rgba(167,139,250,.05)', border:'1px solid #a78bfa22', borderRadius:8 }}>
                        <div style={{ fontSize:10, color:'#a78bfa', fontWeight:800, letterSpacing:0.5, marginBottom:8 }}>📊 RESULT OVERRIDES</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                          <div>
                            <div style={{ fontSize:10, color:'#4a4a70', fontWeight:700, marginBottom:3 }}>% {d.label_a} won</div>
                            <input style={{...inp,marginBottom:0}} type="number" min="0" max="100" placeholder="e.g. 62" value={editFields.final_pct_a??d.final_pct_a??''} onChange={e=>setEditFields(f=>({...f,final_pct_a:e.target.value===''?null:parseFloat(e.target.value)}))}/>
                          </div>
                          <div>
                            <div style={{ fontSize:10, color:'#4a4a70', fontWeight:700, marginBottom:3 }}>% {d.label_b} won</div>
                            <input style={{...inp,marginBottom:0}} type="number" min="0" max="100" placeholder="e.g. 38" value={editFields.final_pct_b??d.final_pct_b??''} onChange={e=>setEditFields(f=>({...f,final_pct_b:e.target.value===''?null:parseFloat(e.target.value)}))}/>
                          </div>
                          <div>
                            <div style={{ fontSize:10, color:'#4a4a70', fontWeight:700, marginBottom:3 }}>Winner side</div>
                            <select style={{...inp,marginBottom:0}} value={editFields.override_winner??d.override_winner??''} onChange={e=>setEditFields(f=>({...f,override_winner:e.target.value||null}))}>
                              <option value="">Auto</option>
                              <option value="A">{d.label_a}</option>
                              <option value="B">{d.label_b}</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:7, marginTop:10 }}>
                      <button onClick={()=>handleSaveEdit(d.id)} style={{ padding:'6px 14px', background:'linear-gradient(135deg,#4fc4b8,#38a89d)', border:'none', borderRadius:7, color:'#0a0a1a', fontWeight:800, fontSize:12, cursor:'pointer' }}>Save</button>
                      <button onClick={()=>{setEditId(null);setEditFields({});}} style={{ padding:'6px 12px', background:'transparent', border:'1px solid #1e1e38', borderRadius:7, color:'#3a3a58', fontSize:12, cursor:'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, marginBottom:6 }}>
                      {isFuture && <div style={{ fontSize:16, color:'#2a2a4a', flexShrink:0, marginTop:2, cursor:'grab', userSelect:'none' }} title="Drag to reorder">⠿</div>}
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                          <span style={{ fontSize:10, fontWeight:800, color:statusColor, letterSpacing:0.5 }}>{statusLabel}</span>
                          <span style={{ fontSize:10, color:'#33334a' }}>{d.date}</span>
                        </div>
                        <div style={{ fontSize:14, fontWeight:700, color:'#d0d0e8', lineHeight:1.35 }}>{d.question}</div>
                        <div style={{ fontSize:11, color:'#33334a', marginTop:4 }}>
                          <span style={{ color:'#4fc4b8' }}>{d.label_a}</span> vs <span style={{ color:'#e8635a' }}>{d.label_b}</span>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                        <button onClick={()=>{setEditId(d.id);setEditFields({});}} style={{ padding:'4px 10px', background:'rgba(255,255,255,.04)', border:'1px solid #1e1e38', borderRadius:6, color:'#6060aa', fontSize:11, cursor:'pointer' }}>✏️</button>
                        {(isFuture||!d.is_closed) && <button onClick={()=>handleDeleteDebateRow(d.id)} style={{ padding:'4px 10px', background:'rgba(232,99,90,.06)', border:'1px solid #e8635a22', borderRadius:6, color:'#e8635a', fontSize:11, cursor:'pointer' }}>🗑️</button>}
                      </div>
                    </div>
                    {(d.final_pct_a!=null||d.override_winner) && (
                      <div style={{ fontSize:11, color:'#33334a', marginTop:4 }}>
                        {d.final_pct_a!=null && <span>📊 {d.final_pct_a}% {d.label_a} / {d.final_pct_b??100-d.final_pct_a}% {d.label_b}</span>}
                        {d.override_winner && <span style={{ marginLeft:8, color:'#f7c948' }}>🏆 {d.override_winner==='A'?d.label_a:d.label_b}</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Comments Tab ── */}
      {tab==='comments' && (
        <div>
          {/* Sort + filter bar */}
          <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
            {/* Side filter */}
            {[['all','All'],['A','Side A'],['B','Side B'],['ai','🤖 AI'],['human','👤 Human']].map(([v,l])=>(
              <button key={v} onClick={()=>setCommentFilter(v)} style={{ padding:'4px 11px', borderRadius:20, border:`1px solid ${commentFilter===v?'#4fc4b8':'#191930'}`, background:commentFilter===v?'#4fc4b811':'transparent', color:commentFilter===v?'#4fc4b8':'#33334a', fontSize:11, fontWeight:commentFilter===v?800:500, cursor:'pointer' }}>{l}</button>
            ))}
            {/* Sort */}
            <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
              {[['newest','Newest'],['oldest','Oldest'],['upvotes','Top']].map(([v,l])=>(
                <button key={v} onClick={()=>setCommentSort(v)} style={{ padding:'4px 10px', borderRadius:20, border:`1px solid ${commentSort===v?'#a78bfa':'#191930'}`, background:commentSort===v?'#a78bfa11':'transparent', color:commentSort===v?'#a78bfa':'#33334a', fontSize:11, fontWeight:commentSort===v?800:500, cursor:'pointer' }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ fontSize:11, color:'#33334a', marginBottom:10 }}>
            {loading ? 'Loading…' : `${(()=>{
              let f = allComments;
              if(commentFilter==='A') f=f.filter(c=>c.side==='A');
              else if(commentFilter==='B') f=f.filter(c=>c.side==='B');
              else if(commentFilter==='ai') f=f.filter(c=>c.user_profiles?.is_ai_seed);
              else if(commentFilter==='human') f=f.filter(c=>!c.user_profiles?.is_ai_seed);
              return f.length;
            })()} comments`}
          </div>
          {loading ? <div style={{ color:'#33334a', textAlign:'center', padding:32 }}>Loading…</div> : (()=>{
            let filtered = [...allComments];
            if(commentFilter==='A') filtered=filtered.filter(c=>c.side==='A');
            else if(commentFilter==='B') filtered=filtered.filter(c=>c.side==='B');
            else if(commentFilter==='ai') filtered=filtered.filter(c=>c.user_profiles?.is_ai_seed);
            else if(commentFilter==='human') filtered=filtered.filter(c=>!c.user_profiles?.is_ai_seed);
            filtered.sort((a,b)=>{
              if(commentSort==='oldest') return new Date(a.created_at)-new Date(b.created_at);
              if(commentSort==='upvotes') return (b.upvote_count??0)-(a.upvote_count??0);
              return new Date(b.created_at)-new Date(a.created_at);
            });
            return filtered.map(c=>{
              const isEditing = editCommentId === c.id;
              const sideCol = c.side==='A'?'#4fc4b8':'#e8635a';
              return (
                <div key={c.id} style={{ background:'#0e0e22', border:'1px solid #191930', borderLeft:`3px solid ${sideCol}44`, borderRadius:'0 10px 10px 0', padding:'10px 14px', marginBottom:7 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:10, color:'#33334a', marginBottom:4, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                        <span style={{ color:sideCol, fontWeight:700 }}>{c.side==='A'?(c.debates?.label_a??'A'):(c.debates?.label_b??'B')}</span>
                        <span>·</span>
                        <span style={{ color:'#5050a0' }}>{c.user_profiles?.display_name||'user'}</span>
                        {c.user_profiles?.is_ai_seed&&<span style={{ fontSize:9, color:'#5050a0', fontWeight:800, background:'#1a1a35', padding:'1px 5px', borderRadius:4 }}>AI</span>}
                        <span>·</span>
                        <span style={{ color:'#2a2a48' }}>{c.debates?.question?.slice(0,36)+'…'}</span>
                        <span>·</span>
                        <span style={{ color:'#2a2a48' }}>{c.debates?.date}</span>
                        <span style={{ color:'#33334a' }}>▲ {c.upvote_count??0}</span>
                      </div>
                      {isEditing ? (
                        <div>
                          <textarea
                            value={editCommentText}
                            onChange={e=>setEditCommentText(e.target.value.slice(0,280))}
                            rows={3}
                            style={{ width:'100%', background:'#0a0a1a', border:`1px solid ${sideCol}44`, borderRadius:7, color:'#d0d0e8', fontSize:13, padding:'8px 10px', resize:'vertical', boxSizing:'border-box', outline:'none', fontFamily:'inherit', lineHeight:1.55 }}
                          />
                          <div style={{ display:'flex', gap:6, marginTop:6, alignItems:'center' }}>
                            <button onClick={()=>handleSaveCommentEdit(c.id)} style={{ padding:'5px 14px', background:'linear-gradient(135deg,#4fc4b8,#38a89d)', border:'none', borderRadius:7, color:'#0a0a1a', fontWeight:900, fontSize:12, cursor:'pointer' }}>Save</button>
                            <button onClick={()=>{setEditCommentId(null);setEditCommentText('');}} style={{ padding:'5px 12px', background:'transparent', border:'1px solid #1a1a30', borderRadius:7, color:'#33334a', fontSize:12, cursor:'pointer' }}>Cancel</button>
                            <span style={{ fontSize:10, color:'#2a2a48', marginLeft:'auto' }}>{editCommentText.length}/280</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize:13, color:'#a8a8c8', lineHeight:1.5 }}>{c.text}</div>
                      )}
                    </div>
                    {!isEditing && (
                      <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                        <button onClick={()=>{setEditCommentId(c.id);setEditCommentText(c.text||'');}} style={{ padding:'4px 9px', background:'rgba(167,139,250,.06)', border:'1px solid #a78bfa22', borderRadius:6, color:'#a78bfa', fontSize:11, cursor:'pointer' }}>✏️</button>
                        <button onClick={()=>handleDeleteCommentRow(c.id)} style={{ padding:'4px 9px', background:'rgba(232,99,90,.06)', border:'1px solid #e8635a22', borderRadius:6, color:'#e8635a', fontSize:11, cursor:'pointer' }}>🗑️</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            });
          })()}
          {!loading&&allComments.length===0&&<div style={{ color:'#33334a', textAlign:'center', padding:32 }}>No comments found.</div>}
        </div>
      )}

      {/* ── Voters Tab ── */}
      {tab==='users' && (
        <div>
          {loading ? (
            <div style={{ color:'#33334a', textAlign:'center', padding:32 }}>Loading voters…</div>
          ) : (
            <>
              {/* Stats bar */}
              <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:10, padding:'10px 16px', flex:1, minWidth:100 }}>
                  <div style={{ fontSize:22, fontWeight:900, color:'#4fc4b8' }}>{voters.length}</div>
                  <div style={{ fontSize:11, color:'#33334a', fontWeight:700 }}>REAL VOTERS</div>
                </div>
                <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:10, padding:'10px 16px', flex:1, minWidth:100 }}>
                  <div style={{ fontSize:22, fontWeight:900, color:'#a78bfa' }}>{selectedVoters.size}</div>
                  <div style={{ fontSize:11, color:'#33334a', fontWeight:700 }}>SELECTED</div>
                </div>
              </div>

              {/* Search */}
              <input
                style={{ ...inp, marginBottom:12 }}
                placeholder="Search by email or name…"
                value={voterSearch}
                onChange={e=>setVoterSearch(e.target.value)}
              />

              {/* Select all / none */}
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <button
                  onClick={()=>setSelectedVoters(new Set(voters.filter(v=>!voterSearch||v.email?.includes(voterSearch)||v.display_name?.toLowerCase().includes(voterSearch.toLowerCase())).map(v=>v.email)))}
                  style={{ padding:'4px 12px', background:'rgba(79,196,184,.08)', border:'1px solid #4fc4b833', borderRadius:20, color:'#4fc4b8', fontSize:11, fontWeight:700, cursor:'pointer' }}
                >Select All</button>
                <button
                  onClick={()=>setSelectedVoters(new Set())}
                  style={{ padding:'4px 12px', background:'transparent', border:'1px solid #1e1e38', borderRadius:20, color:'#33334a', fontSize:11, cursor:'pointer' }}
                >Clear</button>
              </div>

              {/* Voter list */}
              <div style={{ maxHeight:420, overflowY:'auto', marginBottom:20 }}>
                {voters
                  .filter(v=>!voterSearch || v.email?.includes(voterSearch) || v.display_name?.toLowerCase().includes(voterSearch.toLowerCase()))
                  .map(v=>{
                    const isExpanded = expandedVoterId === v.user_id;
                    const history    = voterHistories[v.user_id];
                    return (
                      <div key={v.user_id} style={{ marginBottom:6 }}>
                        {/* Main voter row */}
                        <div
                          style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:selectedVoters.has(v.email)?'rgba(79,196,184,.07)':'#0e0e22', border:`1px solid ${selectedVoters.has(v.email)?'#4fc4b833':'#191930'}`, borderRadius: isExpanded ? '8px 8px 0 0' : 8, cursor:'pointer', transition:'all 0.15s' }}
                        >
                          <div
                            onClick={()=>setSelectedVoters(prev=>{const n=new Set(prev); n.has(v.email)?n.delete(v.email):n.add(v.email); return n;})}
                            style={{ width:16, height:16, border:`2px solid ${selectedVoters.has(v.email)?'#4fc4b8':'#2a2a44'}`, borderRadius:4, background:selectedVoters.has(v.email)?'#4fc4b8':'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}
                          >
                            {selectedVoters.has(v.email) && <span style={{ color:'#0a0a1a', fontSize:10, fontWeight:900 }}>✓</span>}
                          </div>
                          <div
                            onClick={()=>setSelectedVoters(prev=>{const n=new Set(prev); n.has(v.email)?n.delete(v.email):n.add(v.email); return n;})}
                            style={{ flex:1, minWidth:0 }}
                          >
                            <div style={{ fontSize:13, color:'#d0d0e8', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.email}</div>
                            <div style={{ fontSize:11, color:'#33334a' }}>{v.display_name} · {v.vote_count} vote{v.vote_count!==1?'s':''}</div>
                          </div>
                          {/* Expand/collapse history button */}
                          <button
                            onClick={async(e)=>{
                              e.stopPropagation();
                              if(isExpanded){ setExpandedVoterId(null); return; }
                              setExpandedVoterId(v.user_id);
                              if(!voterHistories[v.user_id]){
                                setHistoryLoading(v.user_id);
                                try { const h = await getVoterHistory(v.user_id); setVoterHistories(m=>({...m,[v.user_id]:h})); }
                                catch(err){ console.error(err); setVoterHistories(m=>({...m,[v.user_id]:[]})); }
                                finally{ setHistoryLoading(null); }
                              }
                            }}
                            style={{ padding:'3px 10px', background:'transparent', border:'1px solid #2a2a44', borderRadius:6, color:'#5555a0', fontSize:11, fontWeight:700, cursor:'pointer', flexShrink:0 }}
                          >
                            {isExpanded ? '▲ hide' : '▼ history'}
                          </button>
                        </div>
                        {/* Expanded vote history */}
                        {isExpanded && (
                          <div style={{ background:'#07071a', border:'1px solid #191930', borderTop:'none', borderRadius:'0 0 8px 8px', padding:'10px 12px' }}>
                            {historyLoading === v.user_id ? (
                              <div style={{ color:'#33334a', fontSize:12, padding:8 }}>Loading history…</div>
                            ) : !history || history.length === 0 ? (
                              <div style={{ color:'#33334a', fontSize:12, padding:8 }}>No votes found.</div>
                            ) : (
                              <div>
                                <div style={{ fontSize:10, fontWeight:800, color:'#33334a', letterSpacing:0.8, marginBottom:8 }}>VOTE HISTORY · {history.length} debate{history.length!==1?'s':''}</div>
                                {history.map((h,i)=>(
                                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', borderTop: i>0 ? '1px solid #12122a' : 'none' }}>
                                    <div style={{ fontSize:10, color:'#2a2a44', minWidth:70 }}>{h.date || ''}</div>
                                    <div style={{ flex:1, fontSize:12, color:'#8888c8', lineHeight:1.3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.question}</div>
                                    <div style={{ fontSize:11, fontWeight:800, color: h.side==='A' ? '#4fc4b8' : '#e8635a', flexShrink:0 }}>{h.side==='A' ? h.label_a : h.label_b}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                {voters.filter(v=>!voterSearch||v.email?.includes(voterSearch)||v.display_name?.toLowerCase().includes(voterSearch.toLowerCase())).length===0 && (
                  <div style={{ color:'#33334a', textAlign:'center', padding:20 }}>No voters found.</div>
                )}
              </div>

              {/* Custom email addresses */}
              <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:12, padding:14, marginBottom:16 }}>
                <div style={{ fontSize:11, color:'#6060aa', fontWeight:800, marginBottom:8, letterSpacing:0.5 }}>✉️ SEND TO (combines selected + custom)</div>
                <textarea
                  style={{ ...inp, minHeight:56, resize:'vertical', fontFamily:'monospace', fontSize:12 }}
                  placeholder="Additional emails (comma or newline separated)"
                  value={customEmails}
                  onChange={e=>setCustomEmails(e.target.value)}
                />
                <div style={{ fontSize:11, color:'#4a4a70', fontWeight:700, marginBottom:4, letterSpacing:0.5 }}>SUBJECT <span style={{ color:'#33334a', fontWeight:400 }}>(leave blank for auto)</span></div>
                <input style={{...inp,marginBottom:8}} placeholder={`Today's debate: [question]`} value={voterEmailSubject} onChange={e=>setVoterEmailSubject(e.target.value)}/>
                <div style={{ fontSize:11, color:'#4a4a70', fontWeight:700, marginBottom:4, letterSpacing:0.5 }}>TAGLINE</div>
                <input style={{...inp,marginBottom:8}} value={voterEmailIntro} onChange={e=>setVoterEmailIntro(e.target.value)}/>
                <div style={{ fontSize:11, color:'#4a4a70', fontWeight:700, marginBottom:4, letterSpacing:0.5 }}>CTA BUTTON</div>
                <input style={{...inp,marginBottom:12}} value={voterEmailCta} onChange={e=>setVoterEmailCta(e.target.value)}/>

                <button
                  onClick={async()=>{
                    const extra = customEmails.split(/[\n,]+/).map(e=>e.trim().toLowerCase()).filter(e=>e.includes('@'));
                    const all = [...new Set([...selectedVoters, ...extra])];
                    if(!all.length){alert('Select at least one voter or enter an email address');return;}
                    setBulkEmailStatus('sending'); setBulkEmailError('');
                    try {
                      await sendEmailToAddresses({ emails:all, customSubject:voterEmailSubject||undefined, customIntro:voterEmailIntro||undefined, customCta:voterEmailCta||undefined });
                      setBulkEmailStatus('ok');
                    } catch(err){ setBulkEmailStatus('err'); setBulkEmailError(err.message||'Unknown error'); }
                  }}
                  disabled={bulkEmailStatus==='sending'}
                  style={{ padding:'9px 20px', background:'linear-gradient(135deg,#a78bfa,#7c3aed)', border:'none', borderRadius:9, color:'#fff', fontWeight:900, fontSize:13, cursor:bulkEmailStatus==='sending'?'default':'pointer', opacity:bulkEmailStatus==='sending'?0.7:1 }}
                >
                  {bulkEmailStatus==='sending' ? '⏳ Sending…' : `📨 Send to ${(()=>{const e=customEmails.split(/[\n,]+/).map(x=>x.trim()).filter(x=>x.includes('@'));return new Set([...selectedVoters,...e]).size;})() || '…'} recipient${(()=>{const e=customEmails.split(/[\n,]+/).map(x=>x.trim()).filter(x=>x.includes('@'));return new Set([...selectedVoters,...e]).size!==1?'s':'';})()}`}
                </button>
                {bulkEmailStatus==='ok' && <div style={{ marginTop:10, color:'#4fc4b8', fontSize:13, fontWeight:700 }}>✓ Emails sent successfully!</div>}
                {bulkEmailStatus==='err' && <div style={{ marginTop:10, color:'#e8635a', fontSize:13 }}>✗ Error: {bulkEmailError}</div>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Email Tab ── */}
      {tab==='email' && (
        <div>

          {/* Preview Card */}
          <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:18, marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#8888c8', marginBottom:4 }}>👁️ Preview Email</div>
            <div style={{ fontSize:11, color:'#33334a', marginBottom:14 }}>See exactly what the email will look like for any upcoming date. Uses the template settings below.</div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input
                style={{ ...inp, marginBottom:0, flex:1 }}
                type="date"
                value={emailPreviewDate || (() => { const d=new Date(); d.setDate(d.getDate()+1); return d.toLocaleDateString('en-CA',{timeZone:'America/New_York'}); })()}
                min={today}
                onChange={e=>setEmailPreviewDate(e.target.value)}
              />
              <button
                onClick={handlePreviewEmail}
                disabled={emailPreviewLoading}
                style={{ padding:'9px 18px', background:'#1a1a40', border:'1px solid #3a3a60', borderRadius:9, color:'#d0d0e8', fontWeight:800, fontSize:13, cursor:emailPreviewLoading?'default':'pointer', opacity:emailPreviewLoading?0.6:1, whiteSpace:'nowrap', flexShrink:0 }}
              >
                {emailPreviewLoading ? '⏳ Loading…' : '👁️ Preview'}
              </button>
            </div>
          </div>

          {/* Template Editor */}
          <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:18, marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#8888c8', marginBottom:4 }}>📝 Email Template</div>
            <div style={{ fontSize:11, color:'#33334a', marginBottom:14 }}>Customize email content before sending. Leave blank to use defaults.</div>

            <div style={{ fontSize:11, color:'#4a4a70', fontWeight:700, marginBottom:4, letterSpacing:0.5 }}>SUBJECT LINE <span style={{ color:'#33334a', fontWeight:400 }}>(leave blank for auto)</span></div>
            <input
              style={{ ...inp, marginBottom:14 }}
              type="text"
              placeholder={`Today's debate: [question]`}
              value={emailSubject}
              onChange={e=>setEmailSubject(e.target.value)}
            />

            <div style={{ fontSize:11, color:'#4a4a70', fontWeight:700, marginBottom:4, letterSpacing:0.5 }}>TAGLINE <span style={{ color:'#33334a', fontWeight:400 }}>(below CTA button)</span></div>
            <textarea
              style={{ ...inp, minHeight:60, resize:'vertical', marginBottom:14 }}
              placeholder="Drop your take in the comments. Change someone's mind. 🔥"
              value={emailIntro}
              onChange={e=>setEmailIntro(e.target.value)}
            />

            <div style={{ fontSize:11, color:'#4a4a70', fontWeight:700, marginBottom:4, letterSpacing:0.5 }}>CTA BUTTON TEXT</div>
            <input
              style={{ ...inp, marginBottom:0 }}
              type="text"
              placeholder="Cast Your Vote →"
              value={emailCta}
              onChange={e=>setEmailCta(e.target.value)}
            />
          </div>

          <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:18, marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#8888c8', marginBottom:14 }}>📧 Send Test Daily Email</div>
            <div style={{ fontSize:12, color:'#33334a', marginBottom:12, lineHeight:1.6 }}>
              Triggers the <code style={{ color:'#4fc4b8', background:'#0a0a1a', padding:'1px 5px', borderRadius:3 }}>daily-email</code> Edge Function for a specific address only. Useful for previewing the email before the real send at 9:30 AM EST.
            </div>
            <input
              style={inp}
              type="email"
              placeholder="recipient@example.com"
              value={testEmailAddr}
              onChange={e=>{setTestEmailAddr(e.target.value);setEmailStatus(null);}}
            />
            <button
              onClick={handleSendTestEmail}
              disabled={emailStatus==='sending'}
              style={{ padding:'9px 20px', background:'linear-gradient(135deg,#4fc4b8,#38a89d)', border:'none', borderRadius:9, color:'#0a0a1a', fontWeight:900, fontSize:13, cursor:emailStatus==='sending'?'default':'pointer', opacity:emailStatus==='sending'?0.7:1 }}
            >
              {emailStatus==='sending' ? '⏳ Sending…' : 'Send Test Email'}
            </button>
            {emailStatus==='ok' && <div style={{ marginTop:10, color:'#4fc4b8', fontSize:13, fontWeight:700 }}>✓ Email sent successfully!</div>}
            {emailStatus==='err' && <div style={{ marginTop:10, color:'#e8635a', fontSize:13 }}>✗ Error: {emailError}</div>}
          </div>

          <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:18 }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#8888c8', marginBottom:10 }}>🗓️ Email Schedule</div>
            <div style={{ fontSize:12, color:'#33334a', lineHeight:1.7 }}>
              Daily emails fire automatically via <code style={{ color:'#4fc4b8', background:'#0a0a1a', padding:'1px 5px', borderRadius:3 }}>pg_cron</code> at <strong style={{ color:'#d0d0e8' }}>9:30 AM ET</strong> every day.<br/>
              Requires <code style={{ color:'#4fc4b8', background:'#0a0a1a', padding:'1px 5px', borderRadius:3 }}>RESEND_API_KEY</code> secret set in Supabase Dashboard → Edge Functions → Secrets.
            </div>

            {/* Skip Dates */}
            <div style={{ marginTop:18 }}>
              <div style={{ fontSize:12, color:'#d0d0e8', fontWeight:800, marginBottom:10 }}>🚫 Skip Upcoming Sends</div>
              <div style={{ fontSize:11, color:'#33334a', marginBottom:12 }}>Toggle off any day to prevent the scheduled email from going out. Only affects automatic sends — test emails are unaffected.</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {Array.from({length:7}, (_,i) => {
                  const d = new Date();
                  d.setDate(d.getDate() + i);
                  const dateStr = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
                  const dayLabel = d.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric', timeZone:'America/New_York' });
                  const isSkipped = skipDates.includes(dateStr);
                  const isToday = i === 0;
                  return (
                    <div key={dateStr} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#0a0a1a', border:`1px solid ${isSkipped ? '#3a2020' : '#1a1a30'}`, borderRadius:10, padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ fontSize:13, fontWeight:700, color: isSkipped ? '#4a4a70' : '#d0d0e8', textDecoration: isSkipped ? 'line-through' : 'none' }}>{dayLabel}</div>
                        {isToday && <span style={{ fontSize:10, background:'rgba(79,196,184,0.12)', border:'1px solid rgba(79,196,184,0.3)', color:'#4fc4b8', borderRadius:5, padding:'1px 6px', fontWeight:700 }}>today</span>}
                        {isSkipped && <span style={{ fontSize:10, background:'rgba(232,99,90,0.1)', border:'1px solid rgba(232,99,90,0.3)', color:'#e8635a', borderRadius:5, padding:'1px 6px', fontWeight:700 }}>skipped</span>}
                      </div>
                      <div
                        onClick={()=>{ if(!skipDatesLoading) toggleSkipDate(dateStr); }}
                        style={{ width:42, height:24, borderRadius:12, background: isSkipped ? '#3a1a1a' : '#1a3a30', border:`1.5px solid ${isSkipped ? '#e8635a55' : '#4fc4b855'}`, position:'relative', cursor:skipDatesLoading?'default':'pointer', transition:'all 0.2s', flexShrink:0 }}
                      >
                        <div style={{ position:'absolute', top:3, left: isSkipped ? 3 : 19, width:16, height:16, borderRadius:'50%', background: isSkipped ? '#e8635a' : '#4fc4b8', transition:'left 0.2s' }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop:16, padding:'10px 14px', background:'rgba(247,201,72,.04)', border:'1px solid #f7c94822', borderRadius:10 }}>
              <div style={{ fontSize:11, color:'#f7c948', fontWeight:800, marginBottom:6 }}>⚠️ TIMEZONE NOTE</div>
              <div style={{ fontSize:11, color:'#4a4a70', lineHeight:1.8 }}>
                pg_cron runs in UTC. Supabase cron job should be set to:<br/>
                <code style={{ color:'#4fc4b8' }}>30 13 * * *</code> — fires 9:30 AM EDT (summer, UTC-4)<br/>
                <code style={{ color:'#4fc4b8' }}>30 14 * * *</code> — fires 9:30 AM EST (winter, UTC-5)<br/>
                To verify or fix the schedule, go to <strong style={{ color:'#d0d0e8' }}>Supabase → Database → Cron Jobs</strong> or run in SQL Editor:<br/>
                <code style={{ color:'#4fc4b8', display:'block', marginTop:4 }}>SELECT jobname, schedule, active FROM cron.job;</code>
                <code style={{ color:'#4fc4b8', display:'block' }}>SELECT * FROM cron.job_run_details ORDER BY start_time DESC LIMIT 10;</code>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Settings Tab ── */}
      {tab==='settings' && (
        <div>
          <div style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:20, marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#8888c8', marginBottom:4 }}>🗣️ Comment Policy</div>
            <div style={{ fontSize:11, color:'#33334a', marginBottom:20 }}>Control whether users must post a comment after voting before they can share or browse.</div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#0a0a1a', border:'1px solid #1a1a30', borderRadius:11, padding:'14px 16px' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:'#d0d0e8', marginBottom:3 }}>Require comment after voting</div>
                <div style={{ fontSize:12, color:'#4a4a70' }}>When ON, users must submit a comment before they can share or continue</div>
              </div>
              <div
                onClick={async()=>{
                  const next = !settingCommentReq;
                  setSettingCommentReq(next);
                  setSettingSaving(true); setSettingSaveMsg('');
                  try {
                    await setAppSetting('comment_required', next ? 'true' : 'false');
                    setSettingSaveMsg('✓ Saved');
                  } catch(e) { setSettingCommentReq(!next); setSettingSaveMsg('Error saving'); }
                  finally { setSettingSaving(false); setTimeout(()=>setSettingSaveMsg(''),2500); }
                }}
                style={{ width:48, height:26, borderRadius:13, background: settingCommentReq ? '#4fc4b8' : '#1a1a35', border:`1px solid ${settingCommentReq?'#3ab0a4':'#2a2a44'}`, cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}
              >
                <div style={{ position:'absolute', top:3, left: settingCommentReq ? 24 : 3, width:18, height:18, borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
              </div>
            </div>

            {settingSaveMsg && <div style={{ marginTop:10, fontSize:12, color: settingSaveMsg.startsWith('✓') ? '#4fc4b8' : '#e8635a', fontWeight:700 }}>{settingSaveMsg}</div>}
            {settingSaving && <div style={{ marginTop:10, fontSize:12, color:'#4a4a70' }}>Saving…</div>}
          </div>
        </div>
      )}
    </div>

    {/* Email preview modal */}
    {emailPreviewOpen && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:9999, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'16px' }} onClick={()=>setEmailPreviewOpen(false)}>
        <div style={{ background:'#0e0e22', border:'1px solid #2a2a44', borderRadius:16, width:'100%', maxWidth:580, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 18px', borderBottom:'1px solid #1a1a33', flexShrink:0 }}>
            <div style={{ fontWeight:800, fontSize:15, color:'#d0d0e8' }}>📧 Email Preview</div>
            <button onClick={()=>setEmailPreviewOpen(false)} style={{ background:'none', border:'none', color:'#4a4a70', fontSize:24, cursor:'pointer', lineHeight:1, padding:'0 2px' }}>×</button>
          </div>
          <div style={{ flex:1, overflow:'auto', background:'#07071a' }}>
            <iframe
              srcDoc={emailPreviewHtml}
              style={{ width:'100%', height:'620px', border:'none', display:'block' }}
              title="Email preview"
            />
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── User Profile Screen ──────────────────────────────────────────────────────
function UserProfileScreen({ name, dbName, onBack, leaderStats }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sortBy, setSortBy]     = useState('newest');

  // dbName is the actual display_name in user_profiles; name is the UI label
  const lookupName = dbName || name;

  useEffect(() => {
    setLoading(true);
    getCommentsByDisplayName(lookupName).then(data => { setComments(data); setLoading(false); });
  }, [lookupName]);

  const sortedComments = [...comments].sort((a, b) => {
    if (sortBy === 'newest')  return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'oldest')  return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'upvotes') return (b.upvote_count ?? 0) - (a.upvote_count ?? 0);
    return 0;
  });

  // Pick a stable avatar color from the name
  const avatarColors = ['#4fc4b8','#e8635a','#f7c948','#a78bfa','#34d399','#fb923c'];
  let h = 0; for (let i = 0; i < name.length; i++) h = ((h << 5) + h) ^ name.charCodeAt(i);
  const avatarColor = avatarColors[Math.abs(h) % avatarColors.length];
  const initials = name.slice(0,2).toUpperCase();

  return (
    <div style={{ maxWidth:680, margin:'0 auto', padding:'16px 14px 80px' }}>
      {/* Back button */}
      <button onClick={onBack} style={{ background:'none', border:'none', color:'#4fc4b8', cursor:'pointer', fontSize:13, fontWeight:700, padding:0, marginBottom:20, display:'flex', alignItems:'center', gap:6 }}>
        ← Back to Leaderboard
      </button>

      {/* Avatar + header */}
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:24, padding:'20px 20px', background:'linear-gradient(135deg,rgba(79,196,184,0.07),rgba(79,196,184,0.02))', border:'1px solid #4fc4b820', borderRadius:16 }}>
        <div style={{ width:56, height:56, borderRadius:'50%', background:avatarColor, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:20, color:'#0a0a1a', flexShrink:0, boxShadow:`0 0 20px ${avatarColor}44` }}>
          {initials}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:900, fontSize:20, color:'#d0d0e8', marginBottom:4 }}>{name}</div>
          {leaderStats && (
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              {leaderStats.voices  && <span style={{ fontSize:11, color:'#4fc4b8', fontWeight:700 }}>💬 {leaderStats.voices}</span>}
              {leaderStats.persuasive && <span style={{ fontSize:11, color:'#a78bfa', fontWeight:700 }}>🧠 {leaderStats.persuasive}</span>}
              {leaderStats.streak  && <span style={{ fontSize:11, color:'#f7c948', fontWeight:700 }}>🔥 {leaderStats.streak}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Comment history header + sort */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
        <div style={{ fontWeight:800, fontSize:12, color:'#33334a', letterSpacing:1.2, textTransform:'uppercase' }}>
          {loading ? 'Loading takes…' : `${comments.length} Take${comments.length===1?'':'s'}`}
        </div>
        {!loading && comments.length > 1 && (
          <div style={{ display:'flex', gap:4 }}>
            {[['newest','Newest'],['oldest','Oldest'],['upvotes','Top']].map(([val,label])=>(
              <button key={val} onClick={()=>setSortBy(val)} style={{ padding:'4px 10px', borderRadius:20, border:`1px solid ${sortBy===val?'#4fc4b8':'#191930'}`, background: sortBy===val?'#4fc4b811':'transparent', color: sortBy===val?'#4fc4b8':'#33334a', fontSize:11, fontWeight:700, cursor:'pointer', transition:'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <div style={{ color:'#33334a', textAlign:'center', padding:40 }}>Loading…</div>}

      {!loading && comments.length === 0 && (
        <div style={{ color:'#33334a', textAlign:'center', padding:40, fontSize:13 }}>No takes yet.</div>
      )}

      {!loading && sortedComments.map(c => {
        const d = c.debates;
        const sideColor = c.side === 'A' ? '#4fc4b8' : '#e8635a';
        const sideLabel = c.side === 'A' ? d?.label_a : d?.label_b;
        return (
          <div key={c.id} style={{ background:'#0e0e22', border:'1px solid #191930', borderLeft:`3px solid ${sideColor}55`, borderRadius:'0 12px 12px 0', padding:'13px 16px', marginBottom:10 }}>
            <div style={{ fontSize:10, color:'#33334a', marginBottom:6, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <span style={{ color:sideColor, fontWeight:800 }}>{sideLabel ?? c.side}</span>
              <span>·</span>
              <span style={{ color:'#5050a0' }}>{d?.question?.slice(0,48)}{d?.question?.length>48?'…':''}</span>
              <span style={{ marginLeft:'auto', color:'#2a2a48' }}>{d?.date}</span>
            </div>
            <div style={{ fontSize:13, color:'#a8a8c8', lineHeight:1.6, marginBottom:6 }}>{c.text}</div>
            <div style={{ fontSize:11, color:'#2e2e4e', display:'flex', gap:12 }}>
              <span>▲ {c.upvote_count ?? 0}</span>
              {d?.final_pct_a != null && (
                <span style={{ color: c.side==='A' ? sideColor : '#33334a' }}>
                  Final: {c.side==='A' ? d.final_pct_a : 100-d.final_pct_a}% on their side
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Leaderboard Screen ──────────────────────────────────────────────────────
function LeaderboardScreen({ onViewProfile }) {
  const [tab, setTab] = useState('voices');

  // dbName = the exact display_name in user_profiles (for profile lookup)
  // name   = friendly display label shown in the UI
  const topVoices = [
    { rank: 1, name: 'Josh R.', dbName: 'Joshua Rajaram', stat: '1,247 takes dropped', emoji: '👑' },
    { rank: 2, name: 'Amar S.', dbName: 'Amar Soni', stat: '89 upvotes · 🔥 on fire', emoji: '🥈', badge: '⭐ Rising Star' },
    { rank: 3, name: 'Randy D.', dbName: 'Randy Deopaul', stat: '37 takes dropped', emoji: '🥉', badge: '⭐ Rising Star' },
    { rank: 4, name: 'Nevin L.', dbName: 'Nevin Lall', stat: '29 takes dropped', emoji: null, badge: '⭐ Rising Star' },
    { rank: 5, name: 'Sheena S.', dbName: 'Sheena Singh', stat: '24 takes dropped', emoji: null, badge: '⭐ Rising Star' },
    { rank: 6, name: 'TyrellJ', stat: '847 takes', emoji: null },
    { rank: 7, name: 'sofia_rose', stat: '634 takes', emoji: null },
    { rank: 8, name: 'MarcusB77', stat: '521 takes', emoji: null },
    { rank: 9, name: 'priya_s', stat: '489 takes', emoji: null },
    { rank: 10, name: 'dthornton', stat: '412 takes', emoji: null },
  ];

  const mostPersuasive = [
    { rank: 1, name: 'Josh R.', dbName: 'Joshua Rajaram', stat: '312 minds changed', emoji: '👑' },
    { rank: 2, name: 'Amar S.', dbName: 'Amar Soni', stat: '47 minds changed', emoji: '🥈', badge: '⭐ Rising Star' },
    { rank: 3, name: 'Randy D.', dbName: 'Randy Deopaul', stat: '31 minds changed', emoji: '🥉', badge: '⭐ Rising Star' },
    { rank: 4, name: 'Sheena S.', dbName: 'Sheena Singh', stat: '22 minds changed', emoji: null, badge: '⭐ Rising Star' },
    { rank: 5, name: 'Nevin L.', dbName: 'Nevin Lall', stat: '18 minds changed', emoji: null, badge: '⭐ Rising Star' },
    { rank: 6, name: 'sofia_rose', stat: '127 minds changed', emoji: null },
    { rank: 7, name: 'AnikaVoss', stat: '94 minds changed', emoji: null },
    { rank: 8, name: 'TyrellJ', stat: '88 minds changed', emoji: null },
    { rank: 9, name: 'priya_s', stat: '71 minds changed', emoji: null },
    { rank: 10, name: 'MarcusB77', stat: '58 minds changed', emoji: null },
  ];

  const streakKings = [
    { rank: 1, name: 'Josh R.', dbName: 'Joshua Rajaram', stat: '52-day streak 🔥', emoji: '👑' },
    { rank: 2, name: 'Randy D.', dbName: 'Randy Deopaul', stat: '12-day streak', emoji: '🥈', badge: '⭐ Rising Star' },
    { rank: 3, name: 'Amar S.', dbName: 'Amar Soni', stat: '10-day streak', emoji: '🥉', badge: '⭐ Rising Star' },
    { rank: 4, name: 'Nevin L.', dbName: 'Nevin Lall', stat: '8-day streak', emoji: null, badge: '⭐ Rising Star' },
    { rank: 5, name: 'Sheena S.', dbName: 'Sheena Singh', stat: '7-day streak', emoji: null, badge: '⭐ Rising Star' },
    { rank: 6, name: 'MarcusB77', stat: '47-day streak', emoji: null },
    { rank: 7, name: 'dthornton', stat: '38-day streak', emoji: null },
    { rank: 8, name: 'priya_s', stat: '31-day streak', emoji: null },
    { rank: 9, name: 'TyrellJ', stat: '24-day streak', emoji: null },
    { rank: 10, name: 'AnikaVoss', stat: '19-day streak', emoji: null },
  ];

  const data = tab === 'voices' ? topVoices : tab === 'persuasive' ? mostPersuasive : streakKings;

  // Build per-name stat summary for the profile view
  const allNames = [...new Set([...topVoices, ...mostPersuasive, ...streakKings].map(x=>x.name))];
  const statMap = {};
  allNames.forEach(name => {
    statMap[name] = {
      voices:     topVoices.find(x=>x.name===name)?.stat,
      persuasive: mostPersuasive.find(x=>x.name===name)?.stat,
      streak:     streakKings.find(x=>x.name===name)?.stat,
    };
  });

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '16px 14px 80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{ fontSize: 20 }}>🏆</div>
        <div>
          <div style={{ fontWeight: 900, fontSize: 18, color: '#d0d0e8' }}>Leaderboard</div>
          <div style={{ fontSize: 11, color: '#33334a' }}>Top debaters and influencers</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid #1a1a30', paddingBottom: 12 }}>
        {[['voices', 'Top Voices'], ['persuasive', 'Most Persuasive'], ['streaks', 'Streak Kings']].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              padding: '6px 14px',
              background: tab === k ? '#1a1a35' : 'transparent',
              border: `1px solid ${tab === k ? '#2a2a55' : '#1a1a30'}`,
              borderRadius: 20,
              color: tab === k ? '#d0d0e8' : '#33334a',
              fontWeight: tab === k ? 800 : 500,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {l}
          </button>
        ))}
      </div>

      <div>
        {data.map((item) => (
          <div
            key={item.rank}
            onClick={() => onViewProfile(item.name, item.dbName ?? item.name, statMap[item.name])}
            style={{
              background: '#0e0e22',
              border: '1px solid #191930',
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              cursor: 'pointer',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='#2a2a4a';e.currentTarget.style.background='#101024';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='#191930';e.currentTarget.style.background='#0e0e22';}}
          >
            <div
              style={{
                fontWeight: 900,
                fontSize: 18,
                color: item.emoji ? '#f7c948' : '#33334a',
                minWidth: 40,
                textAlign: 'center',
              }}
            >
              {item.emoji || `#${item.rank}`}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap', marginBottom: 3 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#d0d0e8' }}>{item.name}</span>
                {item.badge && <span style={{ fontSize:10, fontWeight:800, color:'#f7c948', background:'rgba(247,201,72,0.1)', border:'1px solid rgba(247,201,72,0.25)', borderRadius:6, padding:'2px 7px', letterSpacing:0.3 }}>{item.badge}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#7070a0' }}>
                {tab === 'streaks' ? `🔥 ${item.stat}` : item.stat}
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#2a2a48' }}>→</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Closing Countdown ────────────────────────────────────────────────────────
// Shows "Debate closes in X hours Y min" to create urgency before midnight EST.
function ClosingCountdown() {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    function compute() {
      const now = new Date();
      // Midnight EST
      const estMidnight = new Date(now.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
      estMidnight.setDate(estMidnight.getDate() + 1); // next calendar day
      // Convert EST midnight to UTC
      const estOffset = -5 * 60; // EST = UTC-5 (approximate; ignores DST edge case)
      const utcMidnight = new Date(estMidnight.getTime() - estOffset * 60000);
      const diff = utcMidnight - now;
      if (diff <= 0) { setTimeLeft(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      if (h >= 12) { setTimeLeft(''); return; } // only show when <12h left
      setTimeLeft(h > 0 ? `${h}h ${m}m` : `${m}m`);
    }
    compute();
    const t = setInterval(compute, 60000);
    return () => clearInterval(t);
  }, []);

  if (!timeLeft) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 12px', background:'rgba(232,99,90,0.06)', border:'1px solid #e8635a22', borderRadius:9, marginBottom:14, fontSize:12 }}>
      <span style={{ fontSize:15 }}>⏰</span>
      <span style={{ color:'#e8635a', fontWeight:700 }}>Closes in {timeLeft}</span>
      <span style={{ color:'#3a3a58' }}>· Pick your side before this debate locks!</span>
    </div>
  );
}

// ─── Bottom Nav ───────────────────────────────────────────────────────────────
function BottomNav({ screen, onToday, onArchive, onShare, onLeaderboard, onBackoffice, isAdmin }) {
  const tabs = [
    ['🏠','Today','today',onToday],
    ['📅','Archive','archive',onArchive],
    ['🏆','Top','leaderboard',onLeaderboard],
    ['📤','Share','share',onShare],
  ];
  if(isAdmin) tabs.push(['🛠️','Admin','backoffice',onBackoffice]);
  return (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'rgba(8,8,20,0.97)', borderTop:'1px solid #1a1a33', backdropFilter:'blur(12px)', padding:'10px 0 16px', display:'flex', justifyContent:'space-around', zIndex:50 }}>
      {tabs.map(([ic,lab,key,fn])=>(
        <button key={lab} onClick={fn} style={{ background:'none', border:'none', display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor:'pointer', color:screen===key?'#4fc4b8':'#33334a', fontSize:20, transition:'color 0.15s, transform 0.15s', transform:screen===key?'scale(1.1)':'scale(1)' }}>
          <span>{ic}</span>
          <span style={{ fontSize:10, fontWeight:700, letterSpacing:0.5 }}>{lab}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Auth Screen — 3-column: panels left/right, login card centered ───────────
function AuthScreen() {
  const [mode, setMode]       = useState('login');
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [pass, setPass]       = useState('');
  const [err, setErr]         = useState('');
  const [loading, setLoading] = useState(false);
  const googleBtnRef          = useRef(null);

  const inp = { width:'100%', padding:'13px 15px', background:'#0e0e22', border:'1px solid #1e1e3a', borderRadius:12, color:'#d0d0e8', fontSize:16, marginBottom:11, boxSizing:'border-box', outline:'none', fontFamily:'inherit' };

  // Load Google Identity Services and render button (shows pickasyde.com, not supabase URL)
  useEffect(() => {
    let scriptEl = null;
    function initAndRender() {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: '763240201669-ttuh2dvr1l1irobc65tqpo6862hsf4ho.apps.googleusercontent.com',
        callback: async ({ credential }) => {
          setLoading(true);
          setErr('');
          const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: credential });
          if (error) { setErr(error.message); setLoading(false); }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_white',
        size: 'large',
        width: googleBtnRef.current.offsetWidth || 360,
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
      });
    }
    if (window.google) {
      initAndRender();
    } else {
      scriptEl = document.createElement('script');
      scriptEl.src = 'https://accounts.google.com/gsi/client';
      scriptEl.async = true;
      scriptEl.onload = initAndRender;
      document.head.appendChild(scriptEl);
    }
    return () => { if (scriptEl && document.head.contains(scriptEl)) document.head.removeChild(scriptEl); };
  }, []);

  async function submit() {
    setErr('');
    if (!email||!pass) { setErr('All fields required'); return; }
    if (mode==='signup'&&name.trim().length<2) { setErr('Enter a display name (min 2 chars)'); return; }
    setLoading(true);
    if (mode==='signup') {
      const {error} = await supabase.auth.signUp({email,password:pass,options:{data:{full_name:name.trim()}}});
      if (error) setErr(error.message); else setErr('Check your email to confirm, then sign in.');
    } else {
      const {error} = await supabase.auth.signInWithPassword({email,password:pass});
      if (error) setErr(error.message);
    }
    setLoading(false);
  }

  return (
    <div style={{ minHeight:'100vh', background:'#0a0a1a', display:'flex', flexDirection:'column', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <style>{GLOBAL_CSS}</style>

      {/* Login card — centered at top */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 20px 40px' }}>
        <div style={{ width:'100%', maxWidth:380 }}>
          {/* Logo */}
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <div style={{ fontSize:10, fontWeight:800, letterSpacing:4, color:'#2a2a4a', marginBottom:18, textTransform:'uppercase' }}>Welcome to</div>
            <PickASydeLogo size="large" />
            <p style={{ color:'#5050a0', fontSize:14, marginTop:20, fontWeight:500 }}>One question. Two sides. You decide.</p>
          </div>

          {/* Google button — rendered by GIS so dialog shows pickasyde.com, not supabase URL */}
          <div ref={googleBtnRef} style={{ width:'100%', marginBottom:16, display:'flex', justifyContent:'center', minHeight:44 }} />
          {loading && <p style={{ textAlign:'center', color:'#4fc4b8', fontSize:13, marginTop:-8, marginBottom:8 }}>Connecting…</p>}

          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <div style={{ flex:1, height:1, background:'#1e1e3a' }}/><span style={{ color:'#252540', fontSize:12 }}>or</span><div style={{ flex:1, height:1, background:'#1e1e3a' }}/>
          </div>

          {mode==='signup' && <input placeholder="Display name" value={name} onChange={e=>setName(e.target.value)} style={inp}/>}
          <input placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} style={inp}/>
          <input placeholder="Password" type="password" value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==='Enter'&&submit()} style={inp}/>
          {err && <p style={{ color:err.includes('Check your email')?'#4fc4b8':'#e8635a', fontSize:12, margin:'0 0 10px' }}>{err}</p>}

          <button onClick={submit} disabled={loading} style={{ width:'100%', padding:14, background:'linear-gradient(135deg,#4fc4b8,#38a89d)', border:'none', borderRadius:12, color:'#0a0a1a', fontWeight:900, fontSize:15, cursor:'pointer', opacity:loading?0.7:1 }}>
            {loading ? 'Loading…' : mode==='login' ? 'Sign In' : 'Create Account'}
          </button>
          <p style={{ textAlign:'center', color:'#252540', fontSize:13, marginTop:16 }}>
            {mode==='login' ? 'No account? ' : 'Have one? '}
            <span onClick={()=>{setMode(m=>m==='login'?'signup':'login');setErr('');}} style={{ color:'#4fc4b8', cursor:'pointer', fontWeight:600 }}>
              {mode==='login' ? 'Sign up free' : 'Sign in'}
            </span>
          </p>
        </div>
      </div>

      {/* Panels below login — side by side */}
      <div style={{ display:'flex', borderTop:'1px solid #12122a', background:'linear-gradient(180deg,#070712 0%,#0a0a1a 100%)' }}>
        <div style={{ flex:1, borderRight:'1px solid #12122a' }}>
          <AnimatedQuestions compact />
        </div>
        <div style={{ flex:1 }}>
          <PlatformExplainer compact />
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [authUser, setAuthUser]         = useState(null);
  const [userProfile, setUserProfile]   = useState(null);
  const [authLoading, setAuthLoading]   = useState(true);
  const [screen, setScreen]             = useState('today');
  const [profileUser, setProfileUser]   = useState(null); // { name, stats }
  const [archivedDebate, setArchivedDebate] = useState(null);
  const [todayDebate, setTodayDebate]   = useState(null);
  const [yesterdayDebate, setYesterdayDebate] = useState(null);
  const [debateLoading, setDebateLoading] = useState(true);
  const [archive, setArchive]           = useState([]);
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [userVoteMap, setUserVoteMap]   = useState({});
  const [voteCounts, setVoteCounts]     = useState({countA:0,countB:0});
  const [mindsChanged, setMindsChanged]  = useState(0);
  const [comments, setComments]         = useState([]);
  const [commentText, setCommentText]   = useState('');
  const [modState, setModState]         = useState(null);
  const [filterSide, setFilterSide]     = useState('all');
  const [sortBy, setSortBy]             = useState('top');
  const [showBox, setShowBox]           = useState(false);
  const [showVoteButtons, setShowVoteButtons] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [userUpvotes, setUserUpvotes]   = useState(new Set());
  const [aiLoading, setAiLoading]       = useState(null);
  const [aiPersonas, setAiPersonas]     = useState([]);
  const [upcomingDebate, setUpcomingDebate] = useState(null);
  const [streakMilestone, setStreakMilestone] = useState(null); // shown once on milestone days
  const [showShare, setShowShare]       = useState(false);
  const [shareNudge, setShareNudge]     = useState(false);
  const [voteFlash, setVoteFlash]       = useState(null);
  const [sponsorIdx, setSponsorIdx]     = useState(0);
  // Side switch state (max 2 switches per debate)
  const [switchCount, setSwitchCount]   = useState(0);
  const [switchTarget, setSwitchTarget] = useState(null); // { comment } — the comment that triggered switch
  const [switchModal, setSwitchModal]   = useState(false);
  const [switching, setSwitching]       = useState(false);
  // Post-switch comment prompt
  const [showNewSidePrompt, setShowNewSidePrompt] = useState(false);
  // Voter names modal
  const [voterModalDebateId, setVoterModalDebateId] = useState(null);
  const [voterModalLabels, setVoterModalLabels]     = useState({ lA:'', lB:'' });
  const [voterModalPctA, setVoterModalPctA]         = useState(50);
  // Comment required setting
  const [commentRequired, setCommentRequired]       = useState(false);
  // Track if user has posted a comment after their most recent vote (for enforcement)
  const [hasPostedAfterVote, setHasPostedAfterVote] = useState(false);
  const textRef = useRef(null);
  const commentChannelRef = useRef(null);
  const upvoteChannelRef  = useRef(null);
  const todayDebateRef    = useRef(null); // always holds latest todayDebate for the midnight check

  useEffect(() => { const t=setInterval(()=>setSponsorIdx(i=>(i+1)%SPONSORS.length),9000); return()=>clearInterval(t); },[]);
  useEffect(()=>{ getAppSetting('comment_required').then(v=>setCommentRequired(v==='true')).catch(()=>{}); },[]);

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{setAuthUser(session?.user??null);setAuthLoading(false);});
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_,session)=>setAuthUser(session?.user??null));
    return()=>subscription.unsubscribe();
  },[]);
  useEffect(()=>{ if(authUser) getUserProfile().then(p=>setUserProfile(p)).catch(console.error); else setUserProfile(null); },[authUser]);
  useEffect(()=>{ if(authUser) loadTodayDebate(); },[authUser]);

  // Keep ref in sync so the midnight interval always has the latest debate date
  useEffect(()=>{ todayDebateRef.current = todayDebate; },[todayDebate]);

  // Auto-reload when the EST date rolls past midnight (handles page-open-overnight case)
  useEffect(()=>{
    if(!authUser) return;
    const interval = setInterval(()=>{
      const nowDate = new Date().toLocaleDateString('en-CA',{timeZone:'America/New_York'});
      const debateDate = todayDebateRef.current?.date;
      // If we have a loaded debate and today's EST date no longer matches it, reload
      if(debateDate && debateDate !== nowDate) loadTodayDebate();
    }, 60_000); // check every minute
    return ()=>clearInterval(interval);
  },[authUser]);

  async function loadTodayDebate() {
    setDebateLoading(true);
    try {
      const today = await getTodayDebate();
      setTodayDebate(today);
      if (today) {
        const [voteRow,counts,comms,upvs,switched,minds] = await Promise.all([getUserVote(today.id),getVoteCounts(today.id),getCommentsWithStatus(today.id),authUser?getUserUpvotes(today.id):Promise.resolve(new Set()),authUser?hasUserSwitched(today.id):Promise.resolve(false),getMindsChangedCount(today.id)]);
        if(voteRow) setUserVoteMap(m=>({...m,[today.id]:voteRow}));
        setVoteCounts(counts);
        setMindsChanged(minds + 47);
        setComments(addSideLabels(comms,today));
        setUserUpvotes(upvs);
        setSwitchCount(switched||0);
        if(commentChannelRef.current) commentChannelRef.current.unsubscribe();
        commentChannelRef.current = subscribeToComments(today.id, nc=>{
          setComments(prev=>prev.find(c=>c.id===nc.id)?prev:[addSideLabel(nc,today),...prev]);
        });
        if(upvoteChannelRef.current) upvoteChannelRef.current.unsubscribe();
        upvoteChannelRef.current = subscribeToUpvotes(today.id,({commentId,newCount})=>{
          setComments(prev=>prev.map(c=>c.id===commentId?{...c,upvote_count:newCount}:c));
        });
      }
      const arch = await getArchive();
      if (arch.length > 0) {
        const yest = arch[0];
        // If close-debate cron hasn't run yet, final_pct_a is null → compute live pct
        if (yest.final_pct_a == null) {
          const [yCounts, yComms] = await Promise.all([
            getVoteCounts(yest.id).catch(() => null),
            getCommentsWithStatus(yest.id).catch(() => []),
          ]);
          yest._livePct = calcPctA(yest, yComms, null, yCounts);
        }
        setYesterdayDebate(yest);
      }
      // Load tomorrow's teaser
      const upcoming = await getUpcomingDebate();
      setUpcomingDebate(upcoming);
    } catch(err){ console.error('loadTodayDebate error:',err); }
    finally { setDebateLoading(false); }
  }
  useEffect(()=>()=>{commentChannelRef.current?.unsubscribe();upvoteChannelRef.current?.unsubscribe();},[]);

  async function loadArchive() {
    if(archiveLoaded){setScreen('archive');return;}
    try {
      const data=await getArchive(); setArchive(data); setArchiveLoaded(true);
      await Promise.all(data.map(async d=>{ const v=await getUserVote(d.id); if(v) setUserVoteMap(m=>({...m,[d.id]:v})); }));
    } catch(err){console.error('loadArchive error:',err);}
    setScreen('archive');
  }
  async function loadArchivedDetail(debate) {
    setArchivedDebate(debate); setScreen('archived-detail');
    try {
      const [comms, upvs, voteRow] = await Promise.all([
        getCommentsWithStatus(debate.id),
        getUserUpvotes(debate.id),
        authUser ? getUserVote(debate.id) : Promise.resolve(null),
      ]);
      setComments(addSideLabels(comms, debate));
      setUserUpvotes(upvs);
      if (voteRow) setUserVoteMap(m => ({...m, [debate.id]: voteRow}));
    }
    catch(err){console.error('loadArchivedDetail error:',err);}
  }
  function goToToday(fromBackoffice=false) {
    setScreen('today');
    if(todayDebate) getCommentsWithStatus(todayDebate.id).then(comms=>setComments(addSideLabels(comms,todayDebate)));
    if(fromBackoffice) {
      // Refresh yesterday's debate result (may have been edited in backoffice)
      setArchiveLoaded(false);
      loadTodayDebate();
    }
  }
  const addSideLabel  = (c,d) => ({...c,side_label:c.side==='A'?d.label_a:d.label_b});
  const addSideLabels = (cs,d) => cs.map(c=>addSideLabel(c,d));

  const debate    = screen==='archived-detail' ? archivedDebate : todayDebate;
  // Lock today's debate if: explicitly closed by cron/admin, OR its date has already
  // passed in EST (safety net for stale data / cron timing edge cases).
  // Archived debates are never locked — allow voting/commenting on past debates.
  const _todayEST = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const isLocked  = screen === 'today'
    ? ((debate?.is_closed ?? false) || (!!debate?.date && debate.date < _todayEST))
    : false;
  const userVoteRow = debate ? userVoteMap[debate.id] : null;
  const userVote  = userVoteRow?.side ?? null;
  const pctA      = (screen !== 'today' && debate?.final_pct_a != null)
    ? debate.final_pct_a
    : calcPctA(debate, comments, userVote, screen==='today' ? voteCounts : null);

  useEffect(()=>{ if(authUser&&isAdmin(authUser)) getAIPersonas().then(setAiPersonas).catch(console.error); },[authUser]);

  async function handleVote(side) {
    if(!authUser||!debate||isLocked) return;
    const label = side==='A' ? debate.label_a : debate.label_b;
    const color = side==='A' ? '#4fc4b8' : '#e8635a';
    setUserVoteMap(m=>({...m,[debate.id]:{id:`optimistic_${Date.now()}`,side}}));
    setShowVoteButtons(false); setModState(null);
    setHasPostedAfterVote(false); // reset on each new vote
    // Auto-open comment box after voting (post-vote prompt)
    setShowBox(true);
    setShowNewSidePrompt(false);
    if (commentRequired) {
      // When comment is required: skip the flash now — it will fire AFTER they submit their comment
      // Focus textarea quickly so they can start typing right away
      setTimeout(()=>textRef.current?.focus(), 150);
    } else {
      // No comment required: show the celebration flash immediately
      setVoteFlash({side,label,color});
      setTimeout(()=>textRef.current?.focus(),2800); // focus after flash animation
    }
    try {
      const row = userVoteRow ? await changeVote(userVoteRow.id,side) : await castVote(debate.id,side);
      if(!userVoteRow) {
        getUserProfile().then(p=>{
          setUserProfile(p);
          // Check for streak milestones after first-vote profile refresh
          const newStreak = p?.current_streak ?? 0;
          if([2,3,7,14,30,60,100].includes(newStreak)) {
            setStreakMilestone(newStreak);
          }
        }).catch(console.error);
      }
      setUserVoteMap(m=>({...m,[debate.id]:row}));
      getVoteCounts(debate.id).then(setVoteCounts).catch(console.error);
    } catch(err){console.error('handleVote error:',err);setUserVoteMap(m=>({...m,[debate.id]:userVoteRow??null}));}
  }

  async function handleSubmit() {
    if(!authUser||!userVote||submitting||isLocked||!debate) return;
    const text = commentText.trim();
    if(!text){setShowBox(false);setShowNewSidePrompt(false);return;}
    setSubmitting(true); setModState({status:'checking'});
    const result = await moderateComment(text,debate.question);
    if(!result.allowed){setModState({status:'blocked',reason:result.reason});setSubmitting(false);return;}
    setModState({status:'allowed'}); await new Promise(r=>setTimeout(r,600));
    try {
      const nc=await postComment(debate.id,userVote,text);
      setComments(prev=>[addSideLabel(nc,debate),...prev]); setCommentText(''); setModState(null); setShowBox(false); setShowNewSidePrompt(false);
      setHasPostedAfterVote(true);
      // When comment was required: reward them with the vote flash NOW (after they've posted)
      if(commentRequired && userVote && debate) {
        const flashSide  = userVote;
        const flashLabel = flashSide==='A' ? debate.label_a : debate.label_b;
        const flashColor = flashSide==='A' ? '#4fc4b8' : '#e8635a';
        setVoteFlash({side:flashSide, label:flashLabel, color:flashColor});
      }
      // Fire-and-forget: seed fictional engagement for real users' comments ≥ 18 chars
      if(nc?.id && text.length >= 18) {
        supabase.functions.invoke('engage-comment', { body: { comment_id: nc.id } }).catch(()=>{});
      }
    } catch(err){console.error('handleSubmit error:',err);setModState({status:'blocked',reason:'Failed to post. Please try again.'});}
    finally{setSubmitting(false);}
  }

  // Called when user taps "This changed my mind" on an opposite-side comment
  async function handleChangedMyMind(comment) {
    if(!debate||!userVote||switchCount>=2||isLocked) return;
    // Log the signal immediately (analytics — every tap captured)
    await logPersuasionSignal(debate.id, comment.id).catch(console.error);
    setSwitchTarget(comment);
    setSwitchModal(true);
  }

  // Called when user confirms the switch in the modal
  async function handleConfirmSwitch(reasonText) {
    if(!debate||!userVote||!userVoteRow||switching) return;
    const previousSide = userVote;
    const newSide = previousSide==='A' ? 'B' : 'A';
    setSwitching(true);
    try {
      const { voteRow } = await executeSideSwitch({
        voteId: userVoteRow.id,
        debateId: debate.id,
        previousSide,
        newSide,
        persuadingCommentId: switchTarget?.id || null,
        switchReasonText: reasonText || null,
      });
      // Update local state
      setUserVoteMap(m=>({...m,[debate.id]:voteRow}));
      setSwitchCount(c=>c+1);
      // Mark the user's old comment as historical in local state
      setComments(prev=>prev.map(c=>c.user_id===authUser.id&&c.side===previousSide&&c.comment_status==='active' ? {...c,comment_status:'historical'} : c));
      // Update vote counts
      getVoteCounts(debate.id).then(setVoteCounts).catch(console.error);
      // Close modal, clear target
      setSwitchModal(false); setSwitchTarget(null);
      // Prompt for new side comment — pre-fill with the reason they just typed (reduces double-entry friction)
      setCommentText(reasonText||'');
      setModState(null);
      setShowBox(true);
      setShowNewSidePrompt(true);
      setTimeout(()=>textRef.current?.focus(),200);
    } catch(err){console.error('handleConfirmSwitch error:',err);}
    finally{setSwitching(false);}
  }

  async function handleUpvote(commentId) {
    if(!authUser||isLocked) return;
    setComments(prev=>prev.map(c=>c.id===commentId?{...c,upvote_count:c.upvote_count+1}:c));
    setUserUpvotes(s=>new Set([...s,commentId]));
    try { await upvoteComment(commentId); }
    catch(err){ setComments(prev=>prev.map(c=>c.id===commentId?{...c,upvote_count:c.upvote_count-1}:c)); setUserUpvotes(s=>{const n=new Set(s);n.delete(commentId);return n;}); }
  }

  async function handleRemoveUpvote(commentId) {
    if(!authUser||isLocked) return;
    setComments(prev=>prev.map(c=>c.id===commentId?{...c,upvote_count:Math.max(0,c.upvote_count-1)}:c));
    setUserUpvotes(s=>{const n=new Set(s);n.delete(commentId);return n;});
    try { await removeUpvote(commentId); }
    catch(err){ setComments(prev=>prev.map(c=>c.id===commentId?{...c,upvote_count:c.upvote_count+1}:c)); setUserUpvotes(s=>new Set([...s,commentId])); }
  }

  async function handleEdit(commentId, newText) {
    const updated = await updateComment(commentId, newText);
    setComments(prev=>prev.map(c=>c.id===commentId?{...c,text:updated.text}:c));
  }
  async function handleDelete(commentId) {
    await deleteComment(commentId);
    setComments(prev=>prev.filter(c=>c.id!==commentId));
  }

  async function handleAIGenerate(side) {
    if(aiLoading||isLocked||!debate) return;
    setAiLoading(side);
    try {
      const text=await generateAIComment(side,debate.label_a,debate.label_b,debate.question);
      const persona=aiPersonas[Math.floor(Math.random()*aiPersonas.length)];
      if(!persona){alert('No AI persona accounts found.');return;}
      const nc=await seedAIComment(debate.id,side,text,persona.id);
      setComments(prev=>[addSideLabel(nc,debate),...prev]);
    } catch(err){console.error('handleAIGenerate error:',err);alert('AI seed failed: '+err.message);}
    finally{setAiLoading(null);}
  }

  async function handleSignOut() { await supabase.auth.signOut(); setUserVoteMap({}); setUserProfile(null); }

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const userComment = comments.find(c=>c.user_id===authUser?.id);
  const visible = comments.filter(c=>c.text&&(filterSide==='all'||c.side===filterSide)).sort((a,b)=>sortBy==='top'?b.upvote_count-a.upvote_count:new Date(b.created_at)-new Date(a.created_at));
  const streak = userProfile?.current_streak??0;
  const adminUser = authUser&&isAdmin(authUser);
  const activeSponsor = debate?.sponsor_name ? {name:debate.sponsor_name,tagline:debate.sponsor_tagline,letter:debate.sponsor_logo_letter,color:debate.sponsor_color,url:'#'} : SPONSORS[sponsorIdx];

  if(authLoading) return <div style={{minHeight:'100vh',background:'#0a0a1a',display:'flex',alignItems:'center',justifyContent:'center'}}><PickASydeLogo size="large"/></div>;
  if(!authUser) return <AuthScreen/>;

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div className="vs-layout" style={{ fontFamily:"'DM Sans',system-ui,sans-serif", color:'#d0d0e8' }}>

        {/* Side columns removed — panels live below center */}

        {/* CENTER ─────────────────────────────────────────────────────────── */}
        <div className="vs-center">

          {/* Header */}
          <div style={{ background:'rgba(9,9,22,0.97)', borderBottom:'1px solid #1a1a33', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:50, backdropFilter:'blur(12px)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {(screen==='archived-detail'||screen==='backoffice') && <button onClick={()=>screen==='backoffice'?goToToday(true):setScreen('archive')} style={{ background:'none',border:'none',color:'#4fc4b8',cursor:'pointer',fontSize:18,padding:0 }}>←</button>}
              <PickASydeLogo size="small"/>
              {streak>=2 && <span style={{ fontSize:11,fontWeight:800,color:'#f7c948',background:'rgba(247,201,72,.1)',border:'1px solid #f7c94828',borderRadius:6,padding:'2px 8px' }}>{streak}🔥</span>}
            </div>
            <div style={{ display:'flex', gap:7, alignItems:'center' }}>
              {(screen==='archived-detail'||screen==='backoffice') && <button onClick={()=>goToToday(screen==='backoffice')} style={{ background:'rgba(255,255,255,.04)',border:'1px solid #1e1e3a',borderRadius:7,padding:'4px 10px',fontSize:11,fontWeight:700,color:'#55557a',cursor:'pointer' }}>Today</button>}
              <div style={{ position:'relative' }}>
                <button onClick={()=>setShowProfileMenu(v=>!v)} title="Account" style={{ background:'none',border:'none',cursor:'pointer',padding:0 }}>
                  <Av uid={authUser.id} name={userProfile?.display_name||authUser.email} size={30}/>
                </button>
                {showProfileMenu && (
                  <>
                    <div onClick={()=>setShowProfileMenu(false)} style={{ position:'fixed',inset:0,zIndex:99 }}/>
                    <div style={{ position:'absolute',right:0,top:38,zIndex:100,background:'#12122a',border:'1px solid #1e1e3a',borderRadius:11,padding:'6px 0',minWidth:190,boxShadow:'0 8px 32px rgba(0,0,0,0.55)' }}>
                      <div style={{ padding:'8px 14px 6px',borderBottom:'1px solid #1a1a30' }}>
                        <div style={{ fontSize:12,fontWeight:700,color:'#d0d0e8' }}>{userProfile?.display_name||'Account'}</div>
                        <div style={{ fontSize:11,color:'#3a3a58',marginTop:2 }}>{authUser.email}</div>
                      </div>
                      {/* Email opt-out toggle */}
                      <div style={{ padding:'8px 14px',borderBottom:'1px solid #1a1a30',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12 }}>
                        <div>
                          <div style={{ fontSize:12,color:'#6060a0',fontWeight:600 }}>Daily emails</div>
                          <div style={{ fontSize:10,color:'#2a2a48' }}>{userProfile?.email_opt_out ? 'Off' : 'On'}</div>
                        </div>
                        <button
                          onClick={async()=>{
                            const next = !userProfile?.email_opt_out;
                            setUserProfile(p=>({...p,email_opt_out:next}));
                            await supabase.from('user_profiles').update({email_opt_out:next}).eq('id',authUser.id).catch(console.error);
                          }}
                          style={{ width:40,height:22,borderRadius:11,border:'none',cursor:'pointer',background:userProfile?.email_opt_out?'#2a2a44':'#4fc4b8',position:'relative',transition:'background 0.2s',flexShrink:0 }}
                        >
                          <span style={{ position:'absolute',top:3,left:userProfile?.email_opt_out?3:21,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left 0.2s' }}/>
                        </button>
                      </div>
                      <button onClick={()=>{setShowProfileMenu(false);handleSignOut();}} style={{ width:'100%',textAlign:'left',background:'none',border:'none',cursor:'pointer',padding:'9px 14px',fontSize:13,color:'#e8635a',fontWeight:700 }}>
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Backoffice (admin only) ── */}
          {screen==='backoffice' && adminUser && <BackofficeScreen/>}

          {/* ── Leaderboard ── */}
          {screen==='leaderboard' && <LeaderboardScreen onViewProfile={(name,dbName,stats)=>{setProfileUser({name,dbName,stats});setScreen('profile');}}/>}

          {/* ── User Profile ── */}
          {screen==='profile' && profileUser && (
            <UserProfileScreen
              name={profileUser.name}
              dbName={profileUser.dbName}
              leaderStats={profileUser.stats}
              onBack={()=>setScreen('leaderboard')}
            />
          )}

          {/* ── Archive list ── */}
          {screen==='archive' && (
            <div style={{ maxWidth:580, margin:'0 auto', padding:'16px 14px 60px' }}>
              <p style={{ fontSize:13, color:'#3a3a58', marginBottom:16 }}>Browse past debates — you can still vote and comment on any of them.</p>
              {archive.map(d=>{
                const myVote=userVoteMap[d.id]; const pct=d.final_pct_a??50;
                return (
                  <div key={d.id} onClick={()=>loadArchivedDetail(d)}
                    style={{ background:'#0e0e22', border:'1px solid #191930', borderRadius:14, padding:'14px 16px', marginBottom:10, cursor:'pointer', transition:'border-color 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='#2a2a45'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='#191930'}
                  >
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                      <div>
                        <div style={{ fontSize:10, color:'#33334a', fontWeight:700, letterSpacing:0.8, marginBottom:4 }}>{formatDate(d.date)}</div>
                        <div style={{ fontSize:15, fontWeight:700, color:'#d0d0e8', lineHeight:1.3 }}>{d.question}</div>
                      </div>
                      {myVote && <span style={{ fontSize:10, fontWeight:800, padding:'2px 8px', borderRadius:4, background:myVote.side==='A'?'rgba(79,196,184,.12)':'rgba(232,99,90,.12)', color:myVote.side==='A'?'#4fc4b8':'#e8635a', flexShrink:0, marginLeft:10 }}>I picked {myVote.side==='A'?d.label_a:d.label_b}</span>}
                    </div>
                    <VoteBar pctA={pct} lA={d.label_a} lB={d.label_b} animate={false} debateId={d.id} onVoterClick={(id)=>{setVoterModalDebateId(id);setVoterModalLabels({lA:d.label_a,lB:d.label_b});setVoterModalPctA(pct);}} />
                    {d.sponsor_name && <div style={{ marginTop:10, fontSize:10, color:'#33334a', display:'flex', alignItems:'center', gap:5 }}><div style={{ width:14, height:14, borderRadius:3, background:d.sponsor_color||'#888', display:'flex', alignItems:'center', justifyContent:'center', fontSize:7, fontWeight:900, color:'#fff' }}>{d.sponsor_logo_letter}</div>Sponsored by {d.sponsor_name}</div>}
                  </div>
                );
              })}
              {archive.length===0 && <p style={{ textAlign:'center', color:'#33334a', padding:40, fontSize:13 }}>No archived debates yet. Come back tomorrow!</p>}
            </div>
          )}

          {/* ── Today / Archived-detail ── */}
          {screen!=='archive' && screen!=='backoffice' && screen!=='leaderboard' && screen!=='profile' && (
            <div style={{ maxWidth:680, margin:'0 auto', padding:'0 16px 60px', width:'100%' }}>

              {debateLoading && screen==='today' && <div style={{ padding:48, textAlign:'center', color:'#33334a', fontSize:14 }}>Loading today's debate…</div>}

              {!debateLoading && screen==='today' && !todayDebate && (
                <div style={{ padding:48, textAlign:'center' }}>
                  <div style={{ fontSize:30, marginBottom:14 }}>🕐</div>
                  <div style={{ color:'#55557a', fontSize:15, fontWeight:600 }}>No debate scheduled for today.</div>
                  <div style={{ color:'#33334a', fontSize:13, marginTop:6 }}>Check back soon, or browse the archive.</div>
                </div>
              )}

              {debate && (<>

                {/* ── Archived debate prev/next navigation ── */}
                {screen==='archived-detail' && archive.length > 0 && (()=>{
                  const idx = archive.findIndex(d => d.id === archivedDebate?.id);
                  const hasPrev = idx < archive.length - 1; // older
                  const hasNext = idx > 0;                  // newer
                  const prevD   = hasPrev ? archive[idx + 1] : null;
                  const nextD   = hasNext ? archive[idx - 1] : null;
                  if(!hasPrev && !hasNext) return null;
                  return (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginTop:14, marginBottom:4 }}>
                      {/* ← Older */}
                      {hasPrev ? (
                        <button onClick={()=>loadArchivedDetail(prevD)}
                          style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'1px solid #1e1e38', borderRadius:9, padding:'7px 12px', cursor:'pointer', color:'#5555a0', fontSize:12, fontWeight:600, flex:1, textAlign:'left', transition:'border-color 0.15s, color 0.15s', maxWidth:'45%' }}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor='#3a3a60';e.currentTarget.style.color='#9090d0';}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor='#1e1e38';e.currentTarget.style.color='#5555a0';}}
                        >
                          <span style={{ fontSize:16, lineHeight:1 }}>←</span>
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{prevD.question}</span>
                        </button>
                      ) : <div style={{ flex:1, maxWidth:'45%' }} />}

                      {/* counter */}
                      <div style={{ fontSize:10, color:'#2a2a48', fontWeight:700, flexShrink:0, textAlign:'center', whiteSpace:'nowrap' }}>
                        {archive.length - idx} / {archive.length}
                      </div>

                      {/* → Newer */}
                      {hasNext ? (
                        <button onClick={()=>loadArchivedDetail(nextD)}
                          style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:6, background:'none', border:'1px solid #1e1e38', borderRadius:9, padding:'7px 12px', cursor:'pointer', color:'#5555a0', fontSize:12, fontWeight:600, flex:1, textAlign:'right', transition:'border-color 0.15s, color 0.15s', maxWidth:'45%' }}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor='#3a3a60';e.currentTarget.style.color='#9090d0';}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor='#1e1e38';e.currentTarget.style.color='#5555a0';}}
                        >
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nextD.question}</span>
                          <span style={{ fontSize:16, lineHeight:1 }}>→</span>
                        </button>
                      ) : <div style={{ flex:1, maxWidth:'45%' }} />}
                    </div>
                  );
                })()}

                {/* Back-to-today banner — shown when viewing an archived debate */}
                {screen==='archived-detail' && todayDebate && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={goToToday}
                    onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&goToToday()}
                    style={{ marginTop:14, marginBottom:12, background:'linear-gradient(135deg,rgba(79,196,184,0.07),rgba(79,196,184,0.02))', border:'1px solid #4fc4b830', borderRadius:12, padding:'10px 14px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', transition:'border-color 0.15s, background 0.15s' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='#4fc4b855';e.currentTarget.style.background='rgba(79,196,184,0.1)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='#4fc4b830';e.currentTarget.style.background='linear-gradient(135deg,rgba(79,196,184,0.07),rgba(79,196,184,0.02))';}}
                  >
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:9, fontWeight:800, letterSpacing:0.8, color:'#4fc4b8', marginBottom:3, textTransform:'uppercase' }}>🔴 Today's Debate · {formatDate(todayDebate.date)} · <span style={{ color:'#4fc4b855' }}>tap to vote →</span></div>
                      <div style={{ fontSize:13, color:'#7070a0', lineHeight:1.3 }}>{todayDebate.question}</div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:11, fontWeight:800, color:'#4fc4b8' }}>Vote now</div>
                      <div style={{ fontSize:9, color:'#4fc4b866', fontWeight:700 }}>{todayDebate.label_a} vs {todayDebate.label_b}</div>
                    </div>
                  </div>
                )}

                {/* Yesterday banner */}
                {screen==='today' && yesterdayDebate && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={()=>loadArchivedDetail(yesterdayDebate)}
                    onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&loadArchivedDetail(yesterdayDebate)}
                    style={{ marginTop:14, marginBottom:12, background:'#0e0e22', border:'1px solid #191930', borderRadius:12, padding:'10px 14px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', transition:'border-color 0.15s, background 0.15s' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='#2a2a4a';e.currentTarget.style.background='#101024';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='#191930';e.currentTarget.style.background='#0e0e22';}}
                  >
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:9, fontWeight:800, letterSpacing:0.8, color:'#33334a', marginBottom:3 }}>YESTERDAY'S RESULT · {formatDate(yesterdayDebate.date)} · <span style={{ color:'#4fc4b855' }}>tap to view →</span></div>
                      <div style={{ fontSize:13, color:'#7070a0', lineHeight:1.3 }}>{yesterdayDebate.question}</div>
                    </div>
                    {(()=>{
                      const ystPct = yesterdayDebate.final_pct_a ?? yesterdayDebate._livePct;
                      const ystOfficial = yesterdayDebate.final_pct_a != null;
                      const ystWinner = ystPct != null ? (ystPct >= 50 ? yesterdayDebate.label_a : yesterdayDebate.label_b) : null;
                      return (
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontSize:20, fontWeight:900, color:'#4fc4b8' }}>{ystPct != null ? `${Math.round(ystPct)}%` : '—'}</div>
                          <div style={{ fontSize:9, color: ystOfficial ? '#4fc4b8' : '#3a5070', fontWeight:700 }}>
                            {ystOfficial ? `${ystWinner} won` : 'finalizing…'}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Sponsor badge */}
                {activeSponsor && (
                  <a href={activeSponsor.url||'#'} target="_blank" rel="noopener noreferrer" className="sponsor-badge"
                    style={{ display:'flex', alignItems:'center', gap:12, marginTop:screen==='today'?20:14, marginBottom:14, padding:'13px 16px', background:'linear-gradient(135deg,rgba(247,201,72,0.09),rgba(247,201,72,0.04))', border:'1px solid rgba(247,201,72,0.25)', borderRadius:12, textDecoration:'none', cursor:'pointer', position:'relative', overflow:'hidden' }}
                  >
                    <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,rgba(247,201,72,0.5),transparent)' }}/>
                    <div style={{ width:38, height:38, borderRadius:9, flexShrink:0, background:activeSponsor.color||'#333', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, fontWeight:900, color:'#fff', boxShadow:`0 0 14px ${activeSponsor.color||'#333'}66` }}>
                      {activeSponsor.letter}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:9, color:'#f7c948', fontWeight:800, letterSpacing:1.2, textTransform:'uppercase', marginBottom:3 }}>✦ Sponsored by {activeSponsor.name}</div>
                      <div style={{ fontSize:12, color:'#7a7a55', fontWeight:600 }}>{activeSponsor.tagline}</div>
                    </div>
                    <span style={{ fontSize:11, color:'#3a3a30', fontWeight:600, flexShrink:0 }}>↗</span>
                  </a>
                )}

                {/* Locked banner */}
                {isLocked && <div style={{ background:'rgba(232,99,90,.06)', border:'1px solid #e8635a22', borderRadius:10, padding:'9px 14px', marginBottom:14, fontSize:13, color:'#e8635a66', fontWeight:600, textAlign:'center' }}>🔒 This debate is closed · {formatDate(debate.date)} · Votes & comments locked</div>}

                {/* ── Debate card — gradient border, prominent ── */}
                <div className="debate-card-wrap">
                  <div className="debate-card-inner">
                    <div style={{ fontSize:10, fontWeight:800, letterSpacing:1.5, color:'#7070c0', marginBottom:12, textTransform:'uppercase' }}>
                      {isLocked ? `Final Result · ${formatDate(debate.date)}` : `Today's Debate · ${formatDate(debate.date)}`}
                    </div>
                    <h1 style={{ margin:'0 0 22px', fontSize:'clamp(22px,5vw,30px)', fontWeight:900, lineHeight:1.25, color:'#f0f0ff', letterSpacing:'-.5px', fontStyle:'italic' }}>{debate.question}</h1>
                    <VoteBar pctA={pctA} lA={debate.label_a} lB={debate.label_b} voteCounts={voteCounts} mindsChanged={mindsChanged} debateId={debate?.id} onVoterClick={(id)=>{setVoterModalDebateId(id);setVoterModalLabels({lA:debate.label_a,lB:debate.label_b});setVoterModalPctA(pctA);}} />

                    {/* Vote buttons */}
                    {!isLocked && (!userVote||showVoteButtons) && (
                      <div style={{ display:'flex', gap:10, marginTop:20 }}>
                        {(()=>{ const showImgs = !!(debate.image_url_a && debate.image_url_b); return [['A','#4fc4b8',debate.label_a,'✨',showImgs?debate.image_url_a:null],['B','#e8635a',debate.label_b,'🎲',showImgs?debate.image_url_b:null]].map(([s,col,lab,ic,img])=>(
                          <button key={s} onClick={()=>handleVote(s)} className="vote-btn" style={{ flex:1, padding: img ? 0 : '20px 12px', background:`linear-gradient(135deg,${col}22,${col}0a)`, border:`2px solid ${col}`, borderRadius:16, color:col, fontWeight:900, fontSize:'clamp(16px,4vw,20px)', cursor:'pointer', letterSpacing:0.5, fontFamily:"'DM Sans',system-ui,sans-serif", fontStyle:'normal', textShadow:`0 0 16px ${col}99`, boxShadow:`0 0 30px ${col}33`, fontVariantLigatures:'none', overflow:'hidden', display:'flex', flexDirection:'column', alignItems:'stretch' }}>
                            {img && <img src={img} alt={lab} style={{ width:'100%', height:100, objectFit:'cover', display:'block', borderBottom:`2px solid ${col}33`, borderRadius:'14px 14px 0 0', flexShrink:0 }} onError={e=>{e.currentTarget.style.display='none';}} />}
                            <div style={{ padding: img ? '12px 10px' : 0, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>{ic} {lab}</div>
                          </button>
                        )); })()}
                      </div>
                    )}

                    {/* Voted state */}
                    {!isLocked && userVote && !showVoteButtons && (
                      <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:9, flexWrap:'wrap' }}>
                          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:userVote==='A'?'rgba(79,196,184,.12)':'rgba(232,99,90,.12)', border:`1px solid ${userVote==='A'?'#4fc4b844':'#e8635a44'}`, borderRadius:10, padding:'7px 13px', fontSize:13, color:userVote==='A'?'#4fc4b8':'#e8635a', fontWeight:700 }}>
                            ✓ {userVote==='A'?debate.label_a:debate.label_b}
                            {switchCount<2 && !commentRequired && <span onClick={()=>{setShowVoteButtons(true);setShowBox(false);setModState(null);}} style={{ color:'#7070b0', cursor:'pointer', fontSize:11, borderLeft:'1px solid #3a3a58', paddingLeft:8, marginLeft:3, fontWeight:600 }}>change</span>}
                          </div>
                          {(!commentRequired || hasPostedAfterVote) && <button onClick={()=>setShowShare(true)} style={{ background:'rgba(247,201,72,.08)', border:'1px solid #f7c94830', borderRadius:10, padding:'7px 13px', fontSize:12, fontWeight:700, color:'#f7c948', cursor:'pointer' }}>📤 Share</button>}
                          {!showBox && !userComment && (!commentRequired || hasPostedAfterVote) && <button onClick={()=>setShowBox(true)} style={{ fontSize:12, color:'#33334a', background:'none', border:'1px solid #1a1a30', borderRadius:9, padding:'6px 12px', cursor:'pointer' }}>+ comment</button>}
                        </div>
                        {/* Comment-required nudge banner */}
                        {commentRequired && !hasPostedAfterVote && (
                          <div style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(247,201,72,.07)', border:'1px solid #f7c94840', borderRadius:10, padding:'9px 13px', fontSize:12, color:'#f7c948', fontWeight:700 }}>
                            ⏳ Your vote isn't locked yet — drop a quick take below to confirm it, and you'll get to see something pretty cool light up your screen
                          </div>
                        )}
                      </div>
                    )}

                    {/* Locked + voted */}
                    {isLocked && userVote && (
                      <div style={{ marginTop:16, display:'flex', alignItems:'center', gap:9 }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:7, background:userVote==='A'?'rgba(79,196,184,.08)':'rgba(232,99,90,.08)', border:`1px solid ${userVote==='A'?'#4fc4b822':'#e8635a22'}`, borderRadius:10, padding:'6px 12px', fontSize:12, color:userVote==='A'?'#4fc4b8':'#e8635a', fontWeight:700 }}>
                          You picked {userVote==='A'?debate.label_a:debate.label_b}
                        </div>
                        <button onClick={()=>setShowShare(true)} style={{ background:'rgba(247,201,72,.08)', border:'1px solid #f7c94828', borderRadius:9, padding:'6px 12px', fontSize:12, fontWeight:700, color:'#f7c948', cursor:'pointer' }}>📤 Share</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comment box — post-vote prompt or post-switch prompt */}
                {/* Force-show when comment is required and not yet posted (can't let them accidentally hide it) */}
                {((showBox || (commentRequired && !hasPostedAfterVote)) && userVote && !isLocked) && (
                  <div style={{ background:'#0e0e24', border:`1px solid ${userVote==='A'?'#4fc4b825':'#e8635a25'}`, borderRadius:13, padding:16, marginBottom:14 }}>
                    {showNewSidePrompt ? (
                      <p style={{ margin:'0 0 10px', fontSize:13, color:'#8888c8', fontWeight:700 }}>
                        You switched sides. Add a comment supporting your new position. <span style={{ color:'#33334a', fontWeight:400 }}>(optional)</span>
                      </p>
                    ) : (
                      <p style={{ margin:'0 0 10px', fontSize:12, color:'#3e3e5a' }}>
                        Why did you choose <span style={{ color:userVote==='A'?'#4fc4b8':'#e8635a', fontWeight:700 }}>{userVote==='A'?debate.label_a:debate.label_b}</span>? <span style={{ color: commentRequired && !hasPostedAfterVote ? '#f7c948' : '#252540', fontWeight: commentRequired && !hasPostedAfterVote ? 700 : 400 }}>{commentRequired && !hasPostedAfterVote ? '🎆 post it to unlock the celebration' : '(optional — >5 upvotes boosts your side ⚡)'}</span>
                      </p>
                    )}
                    <textarea ref={textRef} placeholder={showNewSidePrompt ? `Make the case for ${userVote==='A'?debate.label_a:debate.label_b}…` : `Why ${userVote==='A'?debate.label_a:debate.label_b}? Make your case…`} value={commentText} onChange={e=>{setCommentText(stripHtml(e.target.value).slice(0,280));if(modState?.status==='blocked')setModState(null);}} rows={3}
                      style={{ width:'100%', background:'#0a0a1a', border:'1px solid #1a1a30', borderRadius:9, color:'#d0d0e8', fontSize:16, padding:'11px 13px', resize:'none', boxSizing:'border-box', outline:'none', fontFamily:'inherit', lineHeight:1.55 }}/>
                    <div style={{ textAlign:'right', fontSize:11, color:'#2e2e48', marginTop:3 }}>{commentText.length}/280</div>
                    {modState && (
                      <div style={{ background:modState.status==='checking'?'#1a1a30':modState.status==='blocked'?'rgba(232,99,90,.1)':'rgba(79,196,184,.08)', border:`1px solid ${modState.status==='checking'?'#2a2a48':modState.status==='blocked'?'#e8635a22':'#4fc4b822'}`, borderRadius:8, padding:'7px 11px', fontSize:12, color:modState.status==='checking'?'#55557a':modState.status==='blocked'?'#e8635a':'#4fc4b8', display:'flex', alignItems:'center', gap:6, marginTop:9, fontWeight:600 }}>
                        <span>{modState.status==='checking'?'⏳':modState.status==='blocked'?'🚫':'✓'}</span>
                        <span>{modState.status==='checking'?'Reviewing comment…':modState.status==='blocked'?(modState.reason||"Comment blocked — personal attacks aren't allowed"):'Approved'}</span>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:9, marginTop:11 }}>
                      <button onClick={handleSubmit} disabled={submitting} style={{ flex:1, padding:11, background:userVote==='A'?'linear-gradient(135deg,#4fc4b8,#38a89d)':'linear-gradient(135deg,#e8635a,#d44a40)', border:'none', borderRadius:9, color:'#0a0a1a', fontWeight:900, fontSize:13, cursor:submitting?'default':'pointer', opacity:submitting?0.7:1 }}>
                        {submitting ? 'Checking…' : 'Post Comment'}
                      </button>
                      {(!commentRequired || hasPostedAfterVote) && <button onClick={()=>{setShowBox(false);setCommentText('');setModState(null);setShowNewSidePrompt(false);}} style={{ padding:'11px 14px', background:'transparent', border:'1px solid #1a1a30', borderRadius:9, color:'#3a3a58', cursor:'pointer', fontSize:13 }}>Skip</button>}
                    </div>
                  </div>
                )}

                {/* ── Retention: "closes in X hours" urgency — shown when user hasn't voted ── */}
                {!isLocked && !userVote && screen==='today' && <ClosingCountdown/>}

                {/* ── Coming Tomorrow — shown for ALL users ── */}
                {!isLocked && screen==='today' && upcomingDebate && (
                  <div style={{ background:'linear-gradient(135deg,rgba(79,196,184,0.06),rgba(79,196,184,0.02))', border:'1px solid #4fc4b820', borderRadius:12, padding:'13px 16px', marginBottom:14 }}>
                    <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.2, color:'#4fc4b8', marginBottom:6, textTransform:'uppercase' }}>🔮 Coming Tomorrow · {formatDate(upcomingDebate.date)}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#a0a0cc', lineHeight:1.35, marginBottom:8 }}>{upcomingDebate.question}</div>
                    <div style={{ fontSize:11, color:'#33334a', marginBottom: userVote ? 0 : 8 }}>
                      <span style={{ color:'#4fc4b8', fontWeight:700 }}>{upcomingDebate.label_a}</span>
                      {' vs '}
                      <span style={{ color:'#e8635a', fontWeight:700 }}>{upcomingDebate.label_b}</span>
                      {' · Debate drops at 9:30 AM EST'}
                    </div>
                    {!userVote && (
                      <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #4fc4b815', fontSize:11, color:'#4fc4b870', fontStyle:'italic' }}>
                        🛌 Sleep on it and then come back and drop your take tomorrow!
                      </div>
                    )}
                  </div>
                )}

                {/* AI seed — admin only */}
                {!isLocked && adminUser && (
                  <div style={{ display:'flex', gap:7, marginBottom:14 }}>
                    {[['A','#4fc4b8',debate.label_a],['B','#e8635a',debate.label_b]].map(([s,col,lab])=>(
                      <button key={s} onClick={()=>handleAIGenerate(s)} disabled={!!aiLoading} style={{ flex:1, padding:6, background:`${col}07`, border:`1px dashed ${col}44`, borderRadius:7, color:col, fontSize:10, fontWeight:700, cursor:aiLoading?'default':'pointer', letterSpacing:0.4, opacity:aiLoading===s?0.6:1 }}>
                        {aiLoading===s?'⏳ generating…':`🤖 Seed: ${lab}`}
                      </button>
                    ))}
                  </div>
                )}

                {/* Filter bar */}
                <div style={{ display:'flex', gap:6, marginBottom:14, alignItems:'center' }}>
                  {[['all','All'],['A',debate.label_a],['B',debate.label_b]].map(([v,l])=>(
                    <button key={v} onClick={()=>setFilterSide(v)} style={{ padding:'5px 13px', background:filterSide===v?(v==='A'?'#4fc4b8':v==='B'?'#e8635a':'#4fc4b8'):'rgba(255,255,255,.03)', border:`1px solid ${filterSide===v?'transparent':'#1a1a30'}`, borderRadius:20, color:filterSide===v?'#0a0a1a':'#3e3e5a', fontSize:12, fontWeight:filterSide===v?800:500, cursor:'pointer' }}>{l}</button>
                  ))}
                  <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
                    {[['top','Top'],['new','New']].map(([v,l])=>(
                      <button key={v} onClick={()=>setSortBy(v)} style={{ padding:'5px 11px', background:sortBy===v?'#1a1a30':'transparent', border:'1px solid #1a1a30', borderRadius:20, color:sortBy===v?'#d0d0e8':'#2e2e48', fontSize:11, cursor:'pointer' }}>{l}</button>
                    ))}
                  </div>
                </div>

                {/* Comments */}
                {visible.length===0
                  ? <p style={{ textAlign:'center', color:'#252540', padding:36, fontSize:13 }}>No comments yet. {isLocked?'Debate is closed.':'Pick a side and start the conversation.'}</p>
                  : visible.map(c=><CommentCard key={c.id} c={c} currentUserId={authUser.id} hasUpvoted={userUpvotes.has(c.id)} onUpvote={handleUpvote} onRemoveUpvote={handleRemoveUpvote} locked={isLocked} onEdit={handleEdit} onDelete={handleDelete} userVote={userVote} canSwitchSides={!!userVote&&switchCount<2&&!isLocked} onChangedMyMind={handleChangedMyMind}/>)
                }
              </>)}
            </div>
          )}

          <BottomNav screen={screen} onToday={goToToday} onArchive={loadArchive} onLeaderboard={()=>setScreen('leaderboard')} isAdmin={!!adminUser} onBackoffice={()=>setScreen('backoffice')} onShare={()=>{
            if(!userVote){ setShareNudge(true); setTimeout(()=>setShareNudge(false),2800); }
            else setShowShare(true);
          }}/>
        </div>

        {/* Explainer below center */}
        <div className="vs-explainer">
          <PlatformExplainer compact/>
        </div>

      </div>

      {/* Vote flash */}
      {voteFlash && <VoteFlashOverlay key={`${voteFlash.side}-${Date.now()}`} side={voteFlash.side} label={voteFlash.label} color={voteFlash.color} onDone={()=>setVoteFlash(null)}/>}

      {/* Vote-first nudge toast */}
      {shareNudge && (
        <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background:'#1a1a33', border:'1px solid #4fc4b844', borderRadius:12, padding:'12px 20px', fontSize:13, fontWeight:700, color:'#4fc4b8', zIndex:200, whiteSpace:'nowrap', boxShadow:'0 4px 24px rgba(0,0,0,0.5)' }}>
          Pick a side first to share your take! 🗳️
        </div>
      )}
      {/* Share modal */}
      {showShare && debate && userVote && <ShareModal debate={debate} vote={userVote} commentText={userComment?.text??null} pct={userVote==='A'?pctA:100-pctA} streak={streak} onClose={()=>setShowShare(false)}/>}

      {/* Streak milestone celebration */}
      {streakMilestone && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:20 }} onClick={()=>setStreakMilestone(null)}>
          <div style={{ background:'#0e0e22', border:'1px solid #2a2a44', borderRadius:20, padding:'36px 28px', maxWidth:340, width:'100%', textAlign:'center', position:'relative' }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:56, marginBottom:8 }}>{streakMilestone>=30?'🏆':streakMilestone>=7?'🔥':'⚡'}</div>
            <div style={{ fontSize:28, fontWeight:900, color:'#f7c948', marginBottom:6, letterSpacing:-0.5 }}>{streakMilestone}-Day Streak!</div>
            <div style={{ fontSize:14, color:'#7070a0', lineHeight:1.6, marginBottom:24 }}>
              {streakMilestone>=30 ? "You're a PickASyde legend. 30 days straight — that's dedication." :
               streakMilestone>=7  ? `A full week of hot takes. Your streak is ${streakMilestone} days strong.` :
                                     `${streakMilestone} days in a row. You're on a roll — keep it going!`}
            </div>
            <button onClick={()=>{setStreakMilestone(null);setShowShare(true);}} style={{ width:'100%', padding:'13px 0', background:'linear-gradient(135deg,#f7c948,#e8b830)', border:'none', borderRadius:11, color:'#0a0a1a', fontWeight:900, fontSize:14, cursor:'pointer', marginBottom:10 }}>
              📤 Share Your Streak
            </button>
            <button onClick={()=>setStreakMilestone(null)} style={{ width:'100%', padding:'11px 0', background:'transparent', border:'1px solid #1e1e38', borderRadius:11, color:'#4a4a6a', fontSize:13, cursor:'pointer' }}>
              Keep it going →
            </button>
          </div>
        </div>
      )}

      {/* Side switch confirmation modal */}
      {switchModal && debate && userVote && (
        <SwitchConfirmModal
          debate={debate}
          previousSide={userVote}
          newSide={userVote==='A'?'B':'A'}
          persuadingComment={switchTarget}
          onConfirm={handleConfirmSwitch}
          onCancel={()=>{setSwitchModal(false);setSwitchTarget(null);}}
          switching={switching}
        />
      )}

      {/* Voter names modal */}
      {voterModalDebateId && (
        <VoterNamesModal
          debateId={voterModalDebateId}
          lA={voterModalLabels.lA}
          lB={voterModalLabels.lB}
          pctA={voterModalPctA}
          onClose={()=>setVoterModalDebateId(null)}
        />
      )}

    </>
  );
}
